import { useState, useEffect, useMemo, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import {
	athleteSportsOf,
	fetchAPI,
	formatDate,
	formatFullName,
	formatRestDisplay,
	formatSportLabel,
} from "../lib/utils";
import { getImageUrl, matchesCoachAthleteSearch } from "../lib/coachUtils";
import { getProgramVisibilityStats } from "../lib/programVisibility";
import { useMediaQuery } from "../lib/useMediaQuery";
import {
	hasContent,
	formatIntervalSecondsLabel,
	getEmomRoundLabel,
	formatEmomItemSummary,
	resolveConditioningResultPresentation,
} from "../lib/conditioningResultUtils";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Switch } from "./ui/switch";
import { Badge } from "./ui/badge";
import { ScrollArea } from "./ui/scroll-area";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from "./ui/collapsible";
import {
	Popover,
	PopoverAnchor,
	PopoverContent,
	PopoverTrigger,
} from "./ui/popover";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "./ui/tooltip";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "./ui/dialog";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "./ui/select";
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetHeader,
	SheetTitle,
	SheetTrigger,
} from "./ui/sheet";
import {
	UserPlus,
	X,
	Loader2,
	LineChart,
	CalendarDays,
	ClipboardCheck,
	Globe,
	Eye,
	EyeOff,
	FolderOpen,
	Plus,
	ChevronDown,
	Trash2,
	ChevronRight,
	ChevronUp,
	UserCog,
	Building2,
	Edit,
	Search,
	Activity,
	Weight,
	ExternalLink,
	Users,
	HeartPulse,
	LayoutGrid,
	List,
	Clock,
} from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "../contexts/LanguageContext";
import { SportsExplainer } from "./sports/SportsExplainer";
import { SportsPicker } from "./sports/SportsPicker";
import AthleteViewPreview from "./AthleteViewPreview";
import { WellnessCoachDialog } from "./wellness/WellnessCoachDialog";
import { WellnessBulkDialog } from "./wellness/WellnessBulkDialog";
import {
	CompetitionEventDialog,
	competitionFormFromEvent,
} from "./planning/CompetitionEventDialog";
import {
	CompetitionParticipantsDialog,
	SharedAthletesBadge,
} from "./planning/CompetitionParticipantsDialog";

// Forma canonica di un coach nei selettori: le due rotte che li elencano
// (`/admin/coaches-with-athletes` e `/groups/access-options`) usano chiavi
// leggermente diverse sui dati storici.
const normalizeCoach = (coach) => ({
	user_id: coach?.user_id || coach?._id || coach?.id,
	name: coach?.name || coach?.email || "Coach",
	email: coach?.email || "",
});

// «+N» della vista elenco: passando il mouse o toccando, mostra tutti gli sport.
// Il ritardo di chiusura permette di scendere dal badge al riquadro senza che
// si chiuda per strada.
function AthleteSportsOverflow({ labels }) {
	const [open, setOpen] = useState(false);
	const closeTimer = useRef(null);

	const cancelClose = () => {
		if (closeTimer.current) clearTimeout(closeTimer.current);
		closeTimer.current = null;
	};
	const scheduleClose = () => {
		cancelClose();
		closeTimer.current = setTimeout(() => setOpen(false), 150);
	};
	useEffect(() => cancelClose, []);

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverAnchor asChild>
				<button
					type="button"
					className="inline-flex items-center rounded-sm border border-border px-1.5 py-0 text-[10px] font-normal leading-4 shrink-0 hover:border-volt/50 hover:text-volt"
					onMouseEnter={() => {
						cancelClose();
						setOpen(true);
					}}
					onMouseLeave={scheduleClose}
					onClick={() => setOpen(true)}
					aria-label={labels.join(", ")}
				>
					+{labels.length}
				</button>
			</PopoverAnchor>
			<PopoverContent
				side="top"
				sideOffset={4}
				className="w-auto max-w-xs p-2 text-xs"
				onOpenAutoFocus={(e) => e.preventDefault()}
				onMouseEnter={cancelClose}
				onMouseLeave={scheduleClose}
			>
				<ul className="space-y-1">
					{labels.map((label) => (
						<li key={label} className="whitespace-nowrap">
							{label}
						</li>
					))}
				</ul>
			</PopoverContent>
		</Popover>
	);
}

export function AthletesTab() {
	const { user } = useAuth();
	const { t, language } = useLanguage();
	const navigate = useNavigate();
	const isAdminUser = user?.is_admin || user?.role === "admin";
	const isDesktopAccess = useMediaQuery("(min-width: 768px)");

	// ── Helpers ──
	const formatLastAccessDateTime = (dateStr) => {
		if (!dateStr) return language === "en" ? "Never" : "Mai";
		const date = new Date(dateStr);
		if (Number.isNaN(date.getTime())) return "-";
		const locale = language === "en" ? "en-US" : "it-IT";
		return date.toLocaleString(locale, {
			year: "numeric",
			month: "2-digit",
			day: "2-digit",
			hour: "2-digit",
			minute: "2-digit",
		});
	};

	// Per la vista elenco: anno a due cifre, cosi' data e ora stanno in una
	// colonna stretta senza troncarsi. Il formato completo resta nel title.
	const formatLastAccessShort = (dateStr) => {
		if (!dateStr) return language === "en" ? "Never" : "Mai";
		const date = new Date(dateStr);
		if (Number.isNaN(date.getTime())) return "-";
		const locale = language === "en" ? "en-US" : "it-IT";
		return date.toLocaleString(locale, {
			year: "2-digit",
			month: "2-digit",
			day: "2-digit",
			hour: "2-digit",
			minute: "2-digit",
		});
	};

	const hasMeaningfulDisplayValue = (value) => {
		if (value === null || value === undefined) return false;
		if (typeof value === "number") return value > 0;
		const text = String(value).trim();
		if (!text) return false;
		if (text === "0" || text === "0.0" || text === "0.00") return false;
		return true;
	};

	const formatProfileMetricValue = (value, unit) => {
		if (!hasMeaningfulDisplayValue(value)) return "-";
		const numericValue = Number(value);
		if (!Number.isFinite(numericValue)) return String(value);

		switch ((unit || "").toLowerCase()) {
			case "seconds": {
				const totalSeconds = Math.max(0, Math.round(numericValue));
				const minutes = Math.floor(totalSeconds / 60);
				const seconds = totalSeconds % 60;
				return minutes > 0
					? `${minutes}:${String(seconds).padStart(2, "0")}`
					: `${seconds}s`;
			}
			case "minutes_seconds": {
				const totalSeconds = Math.max(0, Math.round(numericValue));
				const minutes = Math.floor(totalSeconds / 60);
				const seconds = totalSeconds % 60;
				return `${minutes}:${String(seconds).padStart(2, "0")}`;
			}
			case "kg":
				return `${numericValue} kg`;
			// Rapporto punti/tempo: i decimali distinguono due prestazioni che
			// arrotondate sarebbero lo stesso numero.
			case "hit_factor":
				return `HF ${numericValue.toFixed(2)}`;
			default:
				return String(numericValue);
		}
	};

	const hasIntervalScores = (result) =>
		Array.isArray(result?.interval_scores) && result.interval_scores.length > 0;

	// ── State ──
	const [loading, setLoading] = useState(true);
	const [isRefreshing, setIsRefreshing] = useState(false);
	// `athletes`/`groups` sono l'elenco **della maschera scelta**, non il filtro
	// client: il server risponde col cono di chi chiede (admin compreso) finche'
	// non gli si chiede `scope=all`, cioe' finche' l'admin non seleziona "tutti".
	// E' da qui che pescano anche i selettori (atleti di un gruppo): la maschera
	// decide il cappello, e il cappello vale per tutta la sezione — liste e
	// controlli insieme, o si ricade in «lo vedo in lista e non ci posso operare».
	const [athletes, setAthletes] = useState([]);
	const [groups, setGroups] = useState([]);
	const [programs, setPrograms] = useState([]);
	const [athleteSearch, setAthleteSearch] = useState("");
	const [athleteSort, setAthleteSort] = useState("last_access_desc");
	const [athleteAccessFilter, setAthleteAccessFilter] = useState("all");
	const [athleteSportFilter, setAthleteSportFilter] = useState("all");
	const [athletesScope, setAthletesScope] = useState("mine");
	const [athletesCoachFilter, setAthletesCoachFilter] = useState("all");
	const [athletesViewMode, setAthletesViewMode] = useState(() => {
		if (typeof window === "undefined") return "grid";
		const saved = window.localStorage.getItem("athletes_tab_view_mode");
		return saved === "list" ? "list" : "grid";
	});
	const [selectedAthlete, setSelectedAthlete] = useState(null);
	const [isEditAthleteOpen, setIsEditAthleteOpen] = useState(false);
	const [wellnessAthlete, setWellnessAthlete] = useState(null);
	const [isWellnessOpen, setIsWellnessOpen] = useState(false);
	const [isWellnessBulkOpen, setIsWellnessBulkOpen] = useState(false);
	const [wellnessBulkGroupId, setWellnessBulkGroupId] = useState(null);
	const [sportsOptions, setSportsOptions] = useState([]);
	const [
		selectedAthleteNotificationPrefs,
		setSelectedAthleteNotificationPrefs,
	] = useState(null);
	const [
		isLoadingAthleteNotificationPrefs,
		setIsLoadingAthleteNotificationPrefs,
	] = useState(false);
	const [selectedAthleteInsight, setSelectedAthleteInsight] = useState(null);
	const [isAthleteCalendarOpen, setIsAthleteCalendarOpen] = useState(false);
	const [isAthleteAgendaOpen, setIsAthleteAgendaOpen] = useState(false);
	const [athleteCompetitionsPreview, setAthleteCompetitionsPreview] = useState(
		[],
	);
	const [athleteAgendaResults, setAthleteAgendaResults] = useState([]);
	const [loadingAthleteCalendar, setLoadingAthleteCalendar] = useState(false);
	const [editingCompetition, setEditingCompetition] = useState(null);
	const [participantsEvent, setParticipantsEvent] = useState(null);
	const [participants, setParticipants] = useState([]);
	const [participantsLoading, setParticipantsLoading] = useState(false);
	const [competitionForm, setCompetitionForm] = useState(
		competitionFormFromEvent(null),
	);
	const [loadingAthleteAgenda, setLoadingAthleteAgenda] = useState(false);
	const [expandedAthletePrograms, setExpandedAthletePrograms] = useState({});
	const [exercises, setExercises] = useState([]);
	const [isAthleteProgramsPreviewOpen, setIsAthleteProgramsPreviewOpen] =
		useState(false);
	const [athleteProgramsPreviewState, setAthleteProgramsPreviewState] =
		useState(null);
	const [activeProgramsByAthleteId, setActiveProgramsByAthleteId] = useState(
		{},
	);

	// Groups
	const [isCreateGroupOpen, setIsCreateGroupOpen] = useState(false);
	const [editingGroup, setEditingGroup] = useState(null);
	const [groupForm, setGroupForm] = useState({ name: "", description: "" });
	const [expandedGroups, setExpandedGroups] = useState({});
	const [groupAddAthleteId, setGroupAddAthleteId] = useState(null);
	const [groupAthleteSearchQuery, setGroupAthleteSearchQuery] = useState("");
	const [groupMemberSearchQuery, setGroupMemberSearchQuery] = useState({});
	const [groupMemberSort, setGroupMemberSort] = useState({});
	const [groupRecentMemberChange, setGroupRecentMemberChange] = useState({});
	// Opzioni di condivisione dei gruppi: coach e coach team con cui questo
	// utente puo' condividere. Arrivano da /groups/access-options, che risolve
	// gia' il perimetro lato server (i propri coach team + i coach che ne fanno
	// parte). Prima si chiamavano /teams — admin-only, 403 per ogni coach — e
	// /users/coaches, che non esiste: entrambe cadevano nel .catch e le due
	// tendine restavano vuote, quindi nessun coach poteva condividere un gruppo.
	const [groupCoachOptions, setGroupCoachOptions] = useState([]);
	const [groupCoachTeamOptions, setGroupCoachTeamOptions] = useState([]);
	// Elenco per il filtro "Filtro coach" della vista admin: e' un'altra cosa
	// dalle opzioni di condivisione e ha un'altra fonte.
	const [coachFilterOptions, setCoachFilterOptions] = useState([]);
	const [groupCoachSelection, setGroupCoachSelection] = useState({});
	const [groupCoachTeamSelection, setGroupCoachTeamSelection] = useState({});

	const toggleAthletePrograms = (athleteId) => {
		setExpandedAthletePrograms((prev) => ({
			...prev,
			[athleteId]: !prev[athleteId],
		}));
	};

	// ── Data loading ──
	useEffect(() => {
		loadData();
	}, []);

	useEffect(() => {
		if (typeof window !== "undefined") {
			window.localStorage.setItem("athletes_tab_view_mode", athletesViewMode);
		}
	}, [athletesViewMode]);

	useEffect(() => {
		if (!isAdminUser) {
			// "all" e' solo per l'admin; "mine" e "shared" valgono per tutti.
			setAthletesScope((prev) => (prev === "all" ? "mine" : prev));
			setAthletesCoachFilter("all");
		}
	}, [isAdminUser]);

	// La maschera di gestione completa ("tutti") e' una **richiesta esplicita**,
	// non una proprieta' dell'account: l'elenco di piattaforma si chiede al
	// server solo quando l'admin seleziona quello scope, e per il resto del tempo
	// anche lui vede il proprio cono. Il primo render non rifa' le chiamate che
	// `loadData` ha gia' fatto: si reagisce al **cambio** di scope.
	// `mine` e `shared` sono due tagli dello stesso cono: a cambiare i dati e'
	// solo il passaggio da/verso "tutti".
	const loadedWideRef = useRef(false);
	useEffect(() => {
		if (!isAdminUser) return;
		const wide = athletesScope === "all";
		if (loadedWideRef.current === wide) return;
		loadedWideRef.current = wide;
		let cancelled = false;
		(async () => {
			const qs = wide ? "?scope=all" : "";
			const [scopedAthletes, scopedGroups, scopedOptions] = await Promise.all([
				fetchAPI(`/users/athletes${qs}`).catch(() => null),
				fetchAPI(`/groups${qs}`).catch(() => null),
				fetchAPI(`/groups/access-options${qs}`).catch(() => null),
			]);
			if (cancelled) return;
			if (scopedAthletes) setAthletes(scopedAthletes);
			if (scopedGroups) setGroups(scopedGroups);
			if (scopedOptions) {
				setGroupCoachOptions(
					(scopedOptions.coaches || [])
						.map(normalizeCoach)
						.filter((c) => !!c.user_id),
				);
				setGroupCoachTeamOptions(scopedOptions.teams || []);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [isAdminUser, athletesScope]);

	const loadData = async () => {
		const isInitialLoad = loading;
		if (!isInitialLoad) {
			setIsRefreshing(true);
		}
		try {
			const programScope = isAdminUser ? "all" : "mine";
			// Solo la vista admin ha il filtro per coach, e vuole i coach con
			// atleti: e' una lista diversa da quella con cui si condivide.
			const coachFilterRequest = isAdminUser
				? fetchAPI("/admin/coaches-with-athletes")
						.then((data) => data?.coaches || [])
						.catch(() => [])
				: Promise.resolve([]);

			const [
				athletesData,
				groupsData,
				programsData,
				coachFilterData,
				accessOptionsData,
				exercisesData,
				sportsData,
			] = await Promise.all([
				fetchAPI("/users/athletes").catch(() => []),
				fetchAPI("/groups").catch(() => []),
				fetchAPI(`/programs?scope=${programScope}&compact=true`).catch(
					() => [],
				),
				coachFilterRequest,
				fetchAPI("/groups/access-options").catch(() => ({
					coaches: [],
					teams: [],
				})),
				fetchAPI("/exercises").catch(() => []),
				fetchAPI("/users/sports/list").catch(() => []),
			]);

			setAthletes(athletesData || []);
			setGroups(groupsData || []);
			setPrograms(programsData || []);
			setCoachFilterOptions(
				(coachFilterData || []).map(normalizeCoach).filter((c) => !!c.user_id),
			);
			setGroupCoachOptions(
				(accessOptionsData?.coaches || [])
					.map(normalizeCoach)
					.filter((c) => !!c.user_id),
			);
			setGroupCoachTeamOptions(accessOptionsData?.teams || []);
			setExercises(exercisesData || []);
			setSportsOptions(
				Array.isArray(sportsData)
					? sportsData.map((sport) => ({
							value: sport.value,
							label: sport.label || sport.value,
							// Gli sport solo «legati» (disattivati, o personali di un
							// coach fuori dal tuo perimetro) danno l'etichetta, ma il
							// selettore non li ripropone (docs/sport-config, Fase 3).
							selectable: sport.selectable !== false,
						}))
					: [],
			);
		} catch (error) {
			console.error("Failed to load athletes data:", error);
		} finally {
			setLoading(false);
			setIsRefreshing(false);
		}
	};

	// ── Athlete handlers ──

	// Gli sport mostrati e salvati dal dialog di modifica. Finche' il coach non
	// li tocca valgono quelli dell'atleta (con il ripiego sul campo legacy
	// `sport`); da quando li tocca vale la sua lista cosi' com'e', anche vuota —
	// altrimenti togliendo tutto ricomparirebbe la vecchia primaria.
	const editSportsOf = (athlete) =>
		athlete?._sportsTouched
			? Array.isArray(athlete.sports)
				? athlete.sports
				: []
			: athleteSportsOf(athlete);

	const handleUpdateAthlete = async (e) => {
		e.preventDefault();
		if (!selectedAthlete) return;
		try {
			await fetchAPI(`/users/${selectedAthlete.user_id}`, {
				method: "PUT",
				body: JSON.stringify({
					// La lista intera e solo se il coach l'ha toccata. Mandare
					// `sport` (la vecchia select) cancellava le discipline
					// secondarie; mandare sempre `sports` riscriverebbe con la
					// copia di questa lista un cambio fatto nel frattempo
					// dall'atleta, quando il coach correggeva solo il peso.
					...(selectedAthlete._sportsTouched
						? { sports: editSportsOf(selectedAthlete) }
						: {}),
					gender: selectedAthlete.gender || null,
					weight: selectedAthlete.weight
						? parseFloat(selectedAthlete.weight)
						: null,
					performance_public: !!selectedAthlete.performance_public,
					results_public: selectedAthlete.results_public !== false,
					notes: selectedAthlete.notes,
				}),
			});
			// Si ricarica nello scope in cui si sta guardando, o salvare un atleta
			// farebbe sparire dalla lista tutti gli altri della maschera "tutti".
			const wide = isAdminUser && athletesScope === "all";
			const updated = await fetchAPI(
				`/users/athletes${wide ? "?scope=all" : ""}`,
			);
			setAthletes(updated || []);
			setIsEditAthleteOpen(false);
		} catch (error) {
			console.error("Failed to update athlete:", error);
		}
	};

	useEffect(() => {
		if (!isEditAthleteOpen || !selectedAthlete?.user_id) {
			setSelectedAthleteNotificationPrefs(null);
			setIsLoadingAthleteNotificationPrefs(false);
			return;
		}

		let cancelled = false;
		setIsLoadingAthleteNotificationPrefs(true);
		fetchAPI(`/users/${selectedAthlete.user_id}/notifications`)
			.then((prefs) => {
				if (cancelled) return;
				setSelectedAthleteNotificationPrefs(prefs || null);
			})
			.catch(() => {
				if (cancelled) return;
				setSelectedAthleteNotificationPrefs(null);
			})
			.finally(() => {
				if (cancelled) return;
				setIsLoadingAthleteNotificationPrefs(false);
			});

		return () => {
			cancelled = true;
		};
	}, [isEditAthleteOpen, selectedAthlete?.user_id]);

	// Tutti gli sport praticati, non solo la primaria: un atleta CrossFit + IPSC
	// deve comparire anche filtrando per IPSC (B3 di docs/sport-config).
	const uniqueAthleteSports = useMemo(() => {
		return Array.from(
			new Set(
				(athletes || []).flatMap((a) => athleteSportsOf(a)).filter(Boolean),
			),
		).sort((a, b) =>
			formatSportLabel(a, sportsOptions).localeCompare(
				formatSportLabel(b, sportsOptions),
			),
		);
	}, [athletes, sportsOptions]);

	const getAccessTimestamp = (dateStr) => {
		if (!dateStr) return 0;
		const timestamp = new Date(dateStr).getTime();
		return Number.isNaN(timestamp) ? 0 : timestamp;
	};

	// I coach team di cui faccio parte, come li spedisce /auth/me gia' risolti.
	// Prima qui si leggeva `user.coach_team_ids`, che il payload di /auth/me non
	// ha mai contenuto: il confronto era sempre contro una lista vuota, quindi i
	// gruppi condivisi *per team* venivano scartati dalla UI pur essendo
	// restituiti dall'API. Per gli account dual il campo giusto non sarebbe
	// nemmeno stato quello (lato coach vale `coached_team_ids`), ragione in piu'
	// per farlo risolvere al server. Vedi docs/coach-teams/PLAN.md, Fase 0.
	const myCoachTeamIds = useMemo(
		() =>
			(user?.coached_teams || []).map((team) => team.team_id).filter(Boolean),
		[user],
	);

	const myCoachTeamNameById = useMemo(
		() =>
			Object.fromEntries(
				(user?.coached_teams || [])
					.filter((team) => team.team_id)
					.map((team) => [team.team_id, team.name || team.team_id]),
			),
		[user],
	);

	const filteredAthletes = useMemo(() => {
		const query = athleteSearch.trim().toLowerCase();
		const now = Date.now();
		const recent7Days = now - 7 * 24 * 60 * 60 * 1000;
		const recent30Days = now - 30 * 24 * 60 * 60 * 1000;
		const currentUserId = user?.user_id || user?._id;
		const coachTeamIds = myCoachTeamIds;

		return (athletes || [])
			.filter((a) => {
				// Apply scope filter
				const directCoachIds = Array.from(
					new Set([
						...(a?.coach_ids || []),
						...(a?.coach_id ? [a.coach_id] : []),
					]),
				);
				if (athletesScope === "mine") {
					// Solo gli atleti di cui sono coach diretto.
					return directCoachIds.includes(currentUserId);
				}
				if (athletesScope === "shared") {
					// Quelli che vedo **non** come coach diretto: assegnati a un mio
					// coach team o membri di un gruppo condiviso con me. Senza questo
					// scope erano restituiti dall'API e scartati dalla UI, quindi un
					// coach non aveva nessun modo di vederli.
					return !directCoachIds.includes(currentUserId);
				}
				if (athletesScope === "all") {
					if (!isAdminUser) return false;
					// Show athletes visible via any path (direct or team)
					const directCoachIds = Array.from(
						new Set([
							...(a?.coach_ids || []),
							...(a?.coach_id ? [a.coach_id] : []),
						]),
					);
					const athleteTeamIds = a?.coach_team_ids || [];
					const hasDirectAssignment = directCoachIds.includes(currentUserId);
					const hasTeamAssignment = athleteTeamIds.some((tid) =>
						coachTeamIds.includes(tid),
					);
					return isAdminUser ? true : hasDirectAssignment || hasTeamAssignment;
				}
				return true;
			})
			.filter((a) => {
				// Apply coach filter (only in "all" scope)
				if (
					isAdminUser &&
					athletesScope === "all" &&
					athletesCoachFilter !== "all"
				) {
					const directCoachIds = Array.from(
						new Set([
							...(a?.coach_ids || []),
							...(a?.coach_id ? [a.coach_id] : []),
						]),
					);
					return directCoachIds.includes(athletesCoachFilter);
				}
				return true;
			})
			.filter((a) => {
				return matchesCoachAthleteSearch(a, query);
			})
			.filter((a) => {
				if (athleteSportFilter === "all") return true;
				return athleteSportsOf(a).includes(athleteSportFilter);
			})
			.filter((a) => {
				const accessTs = getAccessTimestamp(a?.last_access_at);
				if (athleteAccessFilter === "all") return true;
				if (athleteAccessFilter === "never") return accessTs === 0;
				if (athleteAccessFilter === "recent_7") return accessTs >= recent7Days;
				if (athleteAccessFilter === "recent_30") {
					return accessTs >= recent30Days;
				}
				if (athleteAccessFilter === "older_30") {
					return accessTs > 0 && accessTs < recent30Days;
				}
				return true;
			})
			.sort((a, b) => {
				if (athleteSort === "name_asc") {
					return (a.name || "").localeCompare(b.name || "");
				}
				if (athleteSort === "name_desc") {
					return (b.name || "").localeCompare(a.name || "");
				}
				if (athleteSort === "created_desc") {
					const createdA = getAccessTimestamp(a.created_at);
					const createdB = getAccessTimestamp(b.created_at);
					return createdB - createdA;
				}
				if (athleteSort === "last_access_asc") {
					return (
						getAccessTimestamp(a.last_access_at) -
						getAccessTimestamp(b.last_access_at)
					);
				}
				return (
					getAccessTimestamp(b.last_access_at) -
					getAccessTimestamp(a.last_access_at)
				);
			});
	}, [
		athleteSearch,
		athleteSportFilter,
		athleteAccessFilter,
		athleteSort,
		athletesScope,
		athletesCoachFilter,
		athletes,
		user,
		myCoachTeamIds,
		isAdminUser,
	]);

	const filteredGroups = useMemo(() => {
		const currentUserId = user?.user_id || user?._id;
		const coachTeamIds = myCoachTeamIds;

		return (groups || []).filter((group) => {
			if (athletesScope === "all") {
				if (!isAdminUser) return false;
				if (athletesCoachFilter === "all") return true;
				const groupCoachIds = Array.from(
					new Set([
						...(group?.coach_ids || []),
						...(group?.coach_id ? [group.coach_id] : []),
					]),
				);
				return groupCoachIds.includes(athletesCoachFilter);
			}

			const groupCoachIds = Array.from(
				new Set([
					...(group?.coach_ids || []),
					...(group?.coach_id ? [group.coach_id] : []),
				]),
			);
			const groupTeamIds = group?.coach_team_ids || [];
			const hasDirectAssignment = groupCoachIds.includes(currentUserId);
			const hasTeamAssignment = groupTeamIds.some((tid) =>
				coachTeamIds.includes(tid),
			);
			const ownsGroup = group.coach_id === currentUserId;
			if (athletesScope === "shared") {
				return (
					!ownsGroup &&
					(!isAdminUser || hasDirectAssignment || hasTeamAssignment)
				);
			}
			return ownsGroup;
		});
	}, [
		groups,
		athletesScope,
		athletesCoachFilter,
		isAdminUser,
		user,
		myCoachTeamIds,
	]);

	// Il profilo prestativo ha una pagina: quella dell'atleta, sezione profilo.
	// Una modale era troppo stretta per livelli, categorie e confronti.
	const openAthleteProfilePreview = (athlete) => {
		navigate(`/athletes/${athlete.user_id}?tab=profile`);
	};

	const loadAthleteCompetitions = async (athleteId) => {
		setLoadingAthleteCalendar(true);
		try {
			const data = await fetchAPI(`/coach/athletes/${athleteId}/competitions`);
			setAthleteCompetitionsPreview(data || []);
		} catch (error) {
			setAthleteCompetitionsPreview([]);
			toast.error(t("calendarLoadError"));
		} finally {
			setLoadingAthleteCalendar(false);
		}
	};

	const openAthleteCalendarPreview = async (athlete) => {
		setSelectedAthleteInsight(athlete);
		setIsAthleteCalendarOpen(true);
		await loadAthleteCompetitions(athlete.user_id);
	};

	// Same "N athletes" pill as the competition hub: the headcount arrives with the
	// race, the roster is fetched only when the coach taps it.
	const openCompetitionParticipants = async (event) => {
		setParticipantsEvent(event);
		setParticipants([]);
		setParticipantsLoading(true);
		try {
			const data = await fetchAPI(
				`/coach/competitions/${event.event_id}/participants`,
			);
			setParticipants(data?.athletes || []);
		} catch {
			toast.error(t("competitionParticipantsError"));
		} finally {
			setParticipantsLoading(false);
		}
	};

	const openEditCompetition = (event) => {
		setEditingCompetition(event);
		setCompetitionForm(competitionFormFromEvent(event));
	};

	// Single-athlete scope here: the race is edited only on this athlete's row,
	// unlike the group calendar where the change fans out to the whole cluster.
	const saveCompetitionEvent = async () => {
		if (!competitionForm.name || !competitionForm.date) {
			toast.error(t("fillRequiredFields"));
			return;
		}
		try {
			await fetchAPI(`/coach/competitions/${editingCompetition.event_id}`, {
				method: "PUT",
				body: JSON.stringify(competitionForm),
			});
			toast.success(t("eventUpdated"));
			setEditingCompetition(null);
			await loadAthleteCompetitions(selectedAthleteInsight.user_id);
		} catch {
			toast.error(t("error"));
		}
	};

	const deleteCompetitionEvent = async (event) => {
		if (!window.confirm(t("competitionHubDeleteConfirm"))) return;
		try {
			await fetchAPI(`/coach/competitions/${event.event_id}`, {
				method: "DELETE",
			});
			toast.success(t("eventDeleted"));
			await loadAthleteCompetitions(selectedAthleteInsight.user_id);
		} catch {
			toast.error(t("error"));
		}
	};

	const openAthleteAgendaPreview = async (athlete) => {
		setSelectedAthleteInsight(athlete);
		setIsAthleteAgendaOpen(true);
		setLoadingAthleteAgenda(true);
		try {
			const data = await fetchAPI(
				`/coach/athletes/${athlete.user_id}/agenda-results`,
			);
			setAthleteAgendaResults(data || []);
		} catch (error) {
			setAthleteAgendaResults([]);
			toast.error(t("agendaLoadError"));
		} finally {
			setLoadingAthleteAgenda(false);
		}
	};

	const getAthleteAssignedPrograms = (athlete) => {
		const athleteGroupIds = filteredGroups
			.filter((g) => (g.athlete_ids || []).includes(athlete.user_id))
			.map((g) => g.group_id);

		return (programs || [])
			.filter(
				(p) =>
					(p.athlete_ids || []).includes(athlete.user_id) ||
					(p.group_ids || []).some((gid) => athleteGroupIds.includes(gid)),
			)
			.sort((a, b) => {
				const dateA = new Date(a.created_at || 0);
				const dateB = new Date(b.created_at || 0);
				return dateB - dateA;
			});
	};

	const getAthleteActivePrograms = async (athleteId) => {
		if (!athleteId) return [];
		if (activeProgramsByAthleteId[athleteId]) {
			return activeProgramsByAthleteId[athleteId];
		}

		const activePrograms = await fetchAPI(
			`/workouts/coach/athletes/${athleteId}/active-programs`,
		).catch(() => []);
		const normalized = Array.isArray(activePrograms) ? activePrograms : [];
		setActiveProgramsByAthleteId((prev) => ({
			...prev,
			[athleteId]: normalized,
		}));
		return normalized;
	};

	const openAthleteProgramsPreview = async (athlete) => {
		const athleteActivePrograms = await getAthleteActivePrograms(
			athlete.user_id,
		);
		if (athleteActivePrograms.length === 0) {
			toast.error(t("noActiveProgram"));
			return;
		}

		const sortedActivePrograms = [...athleteActivePrograms].sort((a, b) => {
			const dateA = new Date(a?.created_at || 0).getTime();
			const dateB = new Date(b?.created_at || 0).getTime();
			return dateB - dateA;
		});

		const previewPrograms = sortedActivePrograms.filter(Boolean);
		const activeProgram = sortedActivePrograms[0] || null;
		const selectedPreviewProgram =
			previewPrograms.find(
				(programItem) => programItem?.program_id === activeProgram?.program_id,
			) ||
			previewPrograms[0] ||
			null;

		setSelectedAthleteInsight(athlete);
		setAthleteProgramsPreviewState({
			program: selectedPreviewProgram,
			programs: previewPrograms,
			weekIndex: 0,
			programCount: previewPrograms.length,
			// Senza questi l'anteprima legge e scrive sull'account del coach:
			// storico vuoto e risultati salvati sulla persona sbagliata.
			athleteId: athlete.user_id,
			athleteName: athlete.name || null,
		});
		setIsAthleteProgramsPreviewOpen(true);
	};

	// ── Group handlers ──

	const handleCreateGroup = async (e) => {
		e.preventDefault();
		if (!groupForm.name.trim()) return;
		try {
			const result = await fetchAPI("/groups", {
				method: "POST",
				body: JSON.stringify(groupForm),
			});
			setGroups((prev) => [...prev, result]);
			setIsCreateGroupOpen(false);
			setGroupForm({ name: "", description: "" });
			toast.success("Gruppo creato");
		} catch (error) {
			toast.error("Errore nella creazione del gruppo");
		}
	};

	const handleUpdateGroup = async (groupId, data) => {
		try {
			const result = await fetchAPI(`/groups/${groupId}`, {
				method: "PUT",
				body: JSON.stringify(data),
			});
			setGroups((prev) =>
				prev.map((g) => (g.group_id === groupId ? result : g)),
			);
			if (data.name !== undefined || data.description !== undefined) {
				setEditingGroup(null);
				toast.success("Gruppo aggiornato");
			}
		} catch (error) {
			toast.error("Errore nell'aggiornamento del gruppo");
		}
	};

	const handleDeleteGroup = async (groupId) => {
		try {
			await fetchAPI(`/groups/${groupId}`, { method: "DELETE" });
			setGroups((prev) => prev.filter((g) => g.group_id !== groupId));
			toast.success("Gruppo eliminato");
		} catch (error) {
			toast.error("Errore nell'eliminazione del gruppo");
		}
	};

	const toggleGroupAthlete = (
		groupId,
		athleteId,
		{ requireConfirm = false } = {},
	) => {
		const group = groups.find((g) => g.group_id === groupId);
		if (!group) return;
		const isIn = (group.athlete_ids || []).includes(athleteId);
		if (isIn && requireConfirm) {
			const athleteName =
				athletes.find((a) => a.user_id === athleteId)?.name || "questo atleta";
			if (!window.confirm(`Rimuovere ${athleteName} da ${group.name}?`)) {
				return;
			}
		}
		const newIds = isIn
			? group.athlete_ids.filter((id) => id !== athleteId)
			: [...(group.athlete_ids || []), athleteId];
		const action = isIn ? "remove" : "add";
		setGroupRecentMemberChange((prev) => ({
			...prev,
			[groupId]: { athleteId, action },
		}));
		setTimeout(() => {
			setGroupRecentMemberChange((prev) => {
				const current = prev[groupId];
				if (!current || current.athleteId !== athleteId) return prev;
				const next = { ...prev };
				delete next[groupId];
				return next;
			});
		}, 2200);
		handleUpdateGroup(groupId, { athlete_ids: newIds });
	};

	const addCoachToGroup = (groupId) => {
		const selectedCoachId = groupCoachSelection[groupId];
		if (!selectedCoachId) {
			toast.error("Seleziona un coach");
			return;
		}
		const group = groups.find((g) => g.group_id === groupId);
		if (!group) return;
		const nextCoachIds = Array.from(
			new Set([...(group.coach_ids || []), selectedCoachId]),
		);
		handleUpdateGroup(groupId, { coach_ids: nextCoachIds });
		setGroupCoachSelection((prev) => ({ ...prev, [groupId]: "" }));
	};

	const removeCoachFromGroup = (groupId, coachId) => {
		const group = groups.find((g) => g.group_id === groupId);
		if (!group) return;
		const nextCoachIds = (group.coach_ids || []).filter((id) => id !== coachId);
		handleUpdateGroup(groupId, { coach_ids: nextCoachIds });
	};

	const addCoachTeamToGroup = (groupId) => {
		const selectedTeamId = groupCoachTeamSelection[groupId];
		if (!selectedTeamId) {
			toast.error("Seleziona un coach team");
			return;
		}
		const group = groups.find((g) => g.group_id === groupId);
		if (!group) return;
		const nextTeamIds = Array.from(
			new Set([...(group.coach_team_ids || []), selectedTeamId]),
		);
		handleUpdateGroup(groupId, { coach_team_ids: nextTeamIds });
		setGroupCoachTeamSelection((prev) => ({ ...prev, [groupId]: "" }));
	};

	const removeCoachTeamFromGroup = (groupId, teamId) => {
		const group = groups.find((g) => g.group_id === groupId);
		if (!group) return;
		const nextTeamIds = (group.coach_team_ids || []).filter(
			(id) => id !== teamId,
		);
		handleUpdateGroup(groupId, { coach_team_ids: nextTeamIds });
	};

	// ── Render ──

	if (loading) {
		return (
			<div className="flex items-center justify-center min-h-[60vh]">
				<div className="spinner"></div>
			</div>
		);
	}

	return (
		<div
			className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-8"
			data-testid="athletes-tab"
		>
			<div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
				<div>
					<h2 className="font-heading text-2xl">{t("athletes")}</h2>
					<p className="text-sm text-muted-foreground">{t("manageAthletes")}</p>
					<div className="flex items-center gap-2 mt-3 flex-wrap">
						<Button
							variant={athletesScope === "mine" ? "default" : "outline"}
							className="rounded-sm"
							onClick={() => setAthletesScope("mine")}
						>
							{t("athletesScopeMine")}
						</Button>
						<Button
							variant={athletesScope === "shared" ? "default" : "outline"}
							className="rounded-sm"
							onClick={() => setAthletesScope("shared")}
						>
							{t("athletesScopeShared")}
						</Button>
						{isAdminUser && (
							<Button
								variant={athletesScope === "all" ? "default" : "outline"}
								className="rounded-sm"
								onClick={() => setAthletesScope("all")}
							>
								{t("athletesScopeAll")}
							</Button>
						)}
						{isAdminUser && athletesScope === "all" && (
							<Select
								value={athletesCoachFilter}
								onValueChange={setAthletesCoachFilter}
							>
								<SelectTrigger className="w-56 bg-secondary border-border rounded-sm text-foreground">
									<SelectValue placeholder="Filtro coach" />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="all">Tutti i coach</SelectItem>
									{coachFilterOptions?.map((coach) => (
										<SelectItem
											key={coach.user_id || coach._id}
											value={coach.user_id || coach._id}
										>
											{coach.name || coach.email}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						)}
					</div>
					{isRefreshing && (
						<div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
							<Loader2 className="w-3.5 h-3.5 animate-spin" />
							<span>Caricamento atleti e programmi...</span>
						</div>
					)}
				</div>
				<Button
					variant="outline"
					size="sm"
					className="rounded-sm self-start md:self-auto"
					onClick={() => {
						setWellnessBulkGroupId(null);
						setIsWellnessBulkOpen(true);
					}}
				>
					<HeartPulse className="w-4 h-4 mr-1.5 text-volt" />
					{t("wellnessBulkOpen")}
				</Button>
			</div>
			<div className="mb-8">
				<div className="flex items-center justify-between mb-4">
					<div className="flex items-center gap-2">
						<FolderOpen className="w-5 h-5 text-volt" />
						<h3 className="font-heading text-lg">{t("groups")}</h3>
						<Badge variant="secondary" className="rounded-sm">
							{filteredGroups.length}
						</Badge>
					</div>
					<Dialog
						open={isCreateGroupOpen}
						onOpenChange={(open) => {
							setIsCreateGroupOpen(open);
							if (!open) setGroupForm({ name: "", description: "" });
						}}
					>
						<DialogTrigger asChild>
							<Button
								variant="outline"
								size="sm"
								className="border-dashed border-volt/50 text-volt rounded-sm"
							>
								<Plus className="w-4 h-4 mr-1" /> Nuovo Gruppo
							</Button>
						</DialogTrigger>
						<DialogContent className="bg-card border-border">
							<DialogDescription className="hidden" />
							<DialogHeader>
								<DialogTitle className="font-heading">
									Crea Nuovo Gruppo
								</DialogTitle>
							</DialogHeader>
							<form onSubmit={handleCreateGroup} className="space-y-4">
								<div className="space-y-2">
									<Label className="label-tactical">Nome</Label>
									<Input
										value={groupForm.name}
										onChange={(e) =>
											setGroupForm({ ...groupForm, name: e.target.value })
										}
										className="bg-secondary border-transparent rounded-sm"
										placeholder="es. Classe Mattina, Competitor"
										required
									/>
								</div>
								<div className="space-y-2">
									<Label className="label-tactical">Descrizione</Label>
									<Textarea
										value={groupForm.description}
										onChange={(e) =>
											setGroupForm({
												...groupForm,
												description: e.target.value,
											})
										}
										className="bg-secondary border-transparent rounded-sm min-h-[80px]"
										placeholder="Descrizione opzionale..."
									/>
								</div>
								<Button
									type="submit"
									className="w-full bg-volt hover:bg-volt/90 rounded-sm"
								>
									Crea Gruppo
								</Button>
							</form>
						</DialogContent>
					</Dialog>
				</div>

				{filteredGroups.length > 0 ? (
					<div className="space-y-3">
						{filteredGroups.map((group) => {
							const isExpanded = expandedGroups[group.group_id];
							const memberSort =
								groupMemberSort[group.group_id] || "last_access_desc";
							const memberSearchQuery = (
								groupMemberSearchQuery[group.group_id] || ""
							)
								.trim()
								.toLowerCase();
							const recentMemberChange =
								groupRecentMemberChange[group.group_id] || null;
							const memberAthletes = athletes.filter((a) =>
								(group.athlete_ids || []).includes(a.user_id),
							);
							const filteredMemberAthletes = memberAthletes.filter((a) => {
								return matchesCoachAthleteSearch(a, memberSearchQuery);
							});
							const sortedFilteredMemberAthletes = [
								...filteredMemberAthletes,
							].sort((a, b) => {
								if (memberSort === "name_asc") {
									return (a?.name || "").localeCompare(b?.name || "");
								}
								if (memberSort === "name_desc") {
									return (b?.name || "").localeCompare(a?.name || "");
								}
								if (memberSort === "last_access_asc") {
									return (
										getAccessTimestamp(a?.last_access_at) -
										getAccessTimestamp(b?.last_access_at)
									);
								}
								return (
									getAccessTimestamp(b?.last_access_at) -
									getAccessTimestamp(a?.last_access_at)
								);
							});
							const assignedCoaches =
								(group.assigned_coaches || []).length > 0
									? group.assigned_coaches
									: [];
							const assignedCoachTeams =
								(group.assigned_coach_teams || []).length > 0
									? group.assigned_coach_teams
									: [];
							// Chi si puo' aggiungere al gruppo: l'elenco della maschera,
							// che di default e' il cono — `PUT /groups/{id}` valida comunque
							// gli ingressi contro il cono di chi scrive.
							const availableGroupAthletes = athletes.filter(
								(a) => !(group.athlete_ids || []).includes(a.user_id),
							);
							const filteredAvailableGroupAthletes =
								availableGroupAthletes.filter((a) =>
									matchesCoachAthleteSearch(a, groupAthleteSearchQuery),
								);
							const isEditing = editingGroup === group.group_id;
							// Solo il proprietario cambia la condivisione: `PUT /groups/{id}`
							// rifiuta `coach_ids`/`coach_team_ids` da chiunque altro. Nome,
							// descrizione e lista atleti restano modificabili da tutti i coach
							// che gestiscono il gruppo: quella e' la delega voluta
							// (docs/coach-teams/DECISIONS.md §2.3).
							const isGroupOwner =
								isAdminUser || group.coach_id === user?.user_id;
							// Per quale dei *miei* team vedo questo gruppo. Non l'elenco di
							// tutti i team con cui e' condiviso: la domanda e' "perche' lo
							// vedo io".
							const sharedViaMyTeams = (group.coach_team_ids || [])
								.map((id) => myCoachTeamNameById[id])
								.filter(Boolean);
							const accessCount =
								assignedCoaches.length + assignedCoachTeams.length;
							const accessControls = (
								<>
									<div className="space-y-2 min-w-0">
										<Label className="label-tactical">Coach con accesso</Label>
										<div className="flex flex-wrap gap-2">
											{assignedCoaches.map((coach) => (
												<TooltipProvider key={coach.user_id}>
													<Tooltip>
														<TooltipTrigger asChild>
															<div className="flex items-center gap-1 px-2 py-1 bg-secondary rounded-sm text-xs cursor-default">
																<span>{coach.name}</span>
																<button
																	onClick={() =>
																		removeCoachFromGroup(
																			group.group_id,
																			coach.user_id,
																		)
																	}
																	className="text-muted-foreground hover:text-blaze"
																>
																	<X className="w-3 h-3" />
																</button>
															</div>
														</TooltipTrigger>
														{coach.email && (
															<TooltipContent>{coach.email}</TooltipContent>
														)}
													</Tooltip>
												</TooltipProvider>
											))}
										</div>
										<div className="flex gap-2">
											<Select
												value={groupCoachSelection[group.group_id] || undefined}
												onValueChange={(v) =>
													setGroupCoachSelection((prev) => ({
														...prev,
														[group.group_id]: v,
													}))
												}
											>
												<SelectTrigger className="bg-secondary border-transparent rounded-sm h-8">
													<SelectValue placeholder="Seleziona coach" />
												</SelectTrigger>
												<SelectContent className="bg-card border-border">
													{groupCoachOptions
														.filter(
															(coach) =>
																!assignedCoaches.some(
																	(c) => c.user_id === coach.user_id,
																),
														)
														.map((coach) => (
															<SelectItem
																key={coach.user_id}
																value={coach.user_id}
															>
																{coach.name}
															</SelectItem>
														))}
												</SelectContent>
											</Select>
											<Button
												variant="outline"
												size="sm"
												className="h-8 rounded-sm"
												onClick={() => addCoachToGroup(group.group_id)}
											>
												Aggiungi
											</Button>
										</div>
									</div>

									<div className="space-y-2 min-w-0">
										<Label className="label-tactical">
											Coach Team con accesso
										</Label>
										<div className="flex flex-wrap gap-2">
											{assignedCoachTeams.map((team) => (
												<div
													key={team.team_id}
													className="flex items-center gap-1 px-2 py-1 bg-secondary rounded-sm text-xs"
												>
													<span>{team.name}</span>
													<button
														onClick={() =>
															removeCoachTeamFromGroup(
																group.group_id,
																team.team_id,
															)
														}
														className="text-muted-foreground hover:text-blaze"
													>
														<X className="w-3 h-3" />
													</button>
												</div>
											))}
										</div>
										<div className="flex gap-2">
											<Select
												value={
													groupCoachTeamSelection[group.group_id] || undefined
												}
												onValueChange={(v) =>
													setGroupCoachTeamSelection((prev) => ({
														...prev,
														[group.group_id]: v,
													}))
												}
											>
												<SelectTrigger className="bg-secondary border-transparent rounded-sm h-8">
													<SelectValue placeholder="Seleziona coach team" />
												</SelectTrigger>
												<SelectContent className="bg-card border-border">
													{groupCoachTeamOptions
														.filter(
															(team) =>
																!assignedCoachTeams.some(
																	(t) => t.team_id === team.team_id,
																),
														)
														.map((team) => (
															<SelectItem
																key={team.team_id}
																value={team.team_id}
															>
																{team.name}
															</SelectItem>
														))}
												</SelectContent>
											</Select>
											<Button
												variant="outline"
												size="sm"
												className="h-8 rounded-sm"
												onClick={() => addCoachTeamToGroup(group.group_id)}
											>
												Aggiungi
											</Button>
										</div>
									</div>
								</>
							);

							return (
								<Card
									key={group.group_id}
									className={`bg-card rounded-sm transition-all ${
										isExpanded || isEditing
											? "border-volt/60 ring-1 ring-volt/30"
											: "border-border"
									}`}
								>
									<CardContent className="p-4">
										<div className="flex flex-wrap items-center justify-between gap-2 sm:flex-nowrap">
											<div className="flex flex-wrap items-center gap-3 w-full min-w-0 sm:w-auto sm:flex-1 sm:flex-nowrap">
												<button
													type="button"
													className="flex items-center gap-3 w-full min-w-0 min-h-11 text-left sm:w-auto sm:flex-1"
													onClick={() =>
														setExpandedGroups((prev) => ({
															...prev,
															[group.group_id]: !prev[group.group_id],
														}))
													}
												>
													{isExpanded ? (
														<ChevronUp className="w-4 h-4 shrink-0 text-muted-foreground" />
													) : (
														<ChevronDown className="w-4 h-4 shrink-0 text-muted-foreground" />
													)}
													<div className="min-w-0">
														<span className="font-heading block whitespace-normal break-words [overflow-wrap:anywhere]">
															{group.name}
														</span>
														{(isExpanded || isEditing) && (
															<span className="text-[10px] uppercase tracking-wider text-volt">
																Gruppo in gestione
															</span>
														)}
														{!isGroupOwner && sharedViaMyTeams.length > 0 && (
															// Perche' questo gruppo e' qui: senza, un coach si
															// trova in elenco atleti e gruppi che non ha creato
															// e non sa da dove arrivino.
															<span className="flex items-center gap-1 text-[10px] text-acid mt-0.5">
																<Building2 className="w-3 h-3 shrink-0" />
																<span className="break-words">
																	{t("programViewSharedVia")}:{" "}
																	{sharedViaMyTeams.join(", ")}
																</span>
															</span>
														)}
														{group.description && (
															<p className="text-xs text-muted-foreground mt-0.5 whitespace-normal break-words [overflow-wrap:anywhere]">
																{group.description}
															</p>
														)}
													</div>
												</button>
												<Badge
													variant="secondary"
													className="rounded-sm shrink-0 whitespace-nowrap"
												>
													{memberAthletes.length} atleti
												</Badge>
												<Popover>
													<PopoverTrigger asChild>
														<button
															type="button"
															className="flex items-center gap-1 text-xs text-muted-foreground hover:text-volt"
														>
															<UserCog className="w-3.5 h-3.5" />
															<span>{assignedCoaches.length}</span>
														</button>
													</PopoverTrigger>
													<PopoverContent
														className="w-60 p-0 bg-card border-border"
														align="start"
													>
														<div className="p-3 border-b border-border">
															<p className="label-tactical">
																Coach con accesso
															</p>
														</div>
														<ScrollArea className="max-h-[180px]">
															<div className="p-2 space-y-1">
																{assignedCoaches.length > 0 ? (
																	assignedCoaches.map((coach) => (
																		<div
																			key={coach.user_id}
																			className="px-2 py-1.5 rounded-sm bg-secondary"
																		>
																			<p className="text-sm truncate">
																				{coach.name}
																			</p>
																			{coach.email && (
																				<p className="text-xs text-muted-foreground truncate">
																					{coach.email}
																				</p>
																			)}
																		</div>
																	))
																) : (
																	<p className="text-xs text-muted-foreground px-2 py-2">
																		Nessun coach assegnato
																	</p>
																)}
															</div>
														</ScrollArea>
													</PopoverContent>
												</Popover>
												<Popover>
													<PopoverTrigger asChild>
														<button
															type="button"
															className="flex items-center gap-1 text-xs text-muted-foreground hover:text-acid"
														>
															<Building2 className="w-3.5 h-3.5" />
															<span>{assignedCoachTeams.length}</span>
														</button>
													</PopoverTrigger>
													<PopoverContent
														className="w-60 p-0 bg-card border-border"
														align="start"
													>
														<div className="p-3 border-b border-border">
															<p className="label-tactical">
																Coach Team con accesso
															</p>
														</div>
														<ScrollArea className="max-h-[180px]">
															<div className="p-2 space-y-1">
																{assignedCoachTeams.length > 0 ? (
																	assignedCoachTeams.map((team) => (
																		<div
																			key={team.team_id}
																			className="px-2 py-1.5 rounded-sm bg-secondary"
																		>
																			<p className="text-sm truncate">
																				{team.name}
																			</p>
																		</div>
																	))
																) : (
																	<p className="text-xs text-muted-foreground px-2 py-2">
																		Nessun coach team assegnato
																	</p>
																)}
															</div>
														</ScrollArea>
													</PopoverContent>
												</Popover>
											</div>
											<div className="flex items-center gap-1 ml-auto shrink-0">
												<Button
													variant="ghost"
													size="icon"
													className="rounded-sm h-11 w-11 sm:h-8 sm:w-8 text-muted-foreground hover:text-volt"
													title={t("wellnessBulkOpen")}
													aria-label={t("wellnessBulkOpen")}
													onClick={() => {
														setWellnessBulkGroupId(group.group_id);
														setIsWellnessBulkOpen(true);
													}}
												>
													<HeartPulse className="w-4 h-4" />
												</Button>
												<Button
													variant="ghost"
													size="icon"
													className="rounded-sm h-11 w-11 sm:h-8 sm:w-8"
													onClick={() => {
														if (isEditing) {
															setEditingGroup(null);
														} else {
															setEditingGroup(group.group_id);
															setGroupForm({
																name: group.name,
																description: group.description || "",
															});
														}
													}}
												>
													<Edit className="w-4 h-4" />
												</Button>
												<Button
													variant="ghost"
													size="icon"
													className="rounded-sm h-11 w-11 sm:h-8 sm:w-8 text-destructive hover:text-destructive"
													onClick={() => {
														if (
															window.confirm(
																`Eliminare il gruppo "${group.name}"?`,
															)
														)
															handleDeleteGroup(group.group_id);
													}}
												>
													<Trash2 className="w-4 h-4" />
												</Button>
											</div>
										</div>

										{/* Edit form */}
										{isEditing && (
											<div className="mt-4 pt-4 border-t border-border space-y-3">
												<div className="space-y-2">
													<Label className="label-tactical">Nome</Label>
													<Input
														value={groupForm.name}
														onChange={(e) =>
															setGroupForm({
																...groupForm,
																name: e.target.value,
															})
														}
														className="bg-secondary border-transparent rounded-sm"
													/>
												</div>
												<div className="space-y-2">
													<Label className="label-tactical">Descrizione</Label>
													<Textarea
														value={groupForm.description}
														onChange={(e) =>
															setGroupForm({
																...groupForm,
																description: e.target.value,
															})
														}
														className="bg-secondary border-transparent rounded-sm min-h-[60px]"
													/>
												</div>
												<div className="flex gap-2">
													<Button
														size="sm"
														className="bg-volt hover:bg-volt/90 rounded-sm"
														onClick={() =>
															handleUpdateGroup(group.group_id, {
																name: groupForm.name,
																description: groupForm.description,
															})
														}
													>
														Salva
													</Button>
													<Button
														size="sm"
														variant="ghost"
														className="rounded-sm"
														onClick={() => setEditingGroup(null)}
													>
														Annulla
													</Button>
												</div>
											</div>
										)}

										{/* Expanded: athlete list + add */}
										{isExpanded && (
											<div className="mt-4 pt-4 border-t border-border">
												{/* Access management (coach & team) — hidden by default */}
												<div className="mb-4">
													{isDesktopAccess ? (
														<Collapsible>
															<CollapsibleTrigger asChild>
																<button
																	type="button"
																	className="group w-full flex items-center justify-between gap-2 px-3 py-2 rounded-sm bg-secondary/60 border border-border/60 hover:bg-secondary text-left transition-colors"
																>
																	<span className="flex items-center gap-2 min-w-0">
																		<UserCog className="w-4 h-4 text-muted-foreground shrink-0" />
																		<span className="text-xs font-medium uppercase tracking-wider truncate">
																			Gestione accessi coach &amp; team
																		</span>
																		<Badge
																			variant="secondary"
																			className="rounded-sm shrink-0"
																		>
																			{accessCount}
																		</Badge>
																	</span>
																	<ChevronDown className="w-4 h-4 text-muted-foreground shrink-0 transition-transform group-data-[state=open]:rotate-180" />
																</button>
															</CollapsibleTrigger>
															<CollapsibleContent>
																<div className="mt-3 grid grid-cols-1 lg:grid-cols-2 gap-4">
																	{isGroupOwner ? (
																		accessControls
																	) : (
																		<p className="text-xs text-muted-foreground lg:col-span-2">
																			{t("groupSharingOwnerOnly")}
																		</p>
																	)}
																</div>
															</CollapsibleContent>
														</Collapsible>
													) : (
														<Sheet>
															<SheetTrigger asChild>
																<button
																	type="button"
																	className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-sm bg-secondary/60 border border-border/60 text-left transition-colors"
																>
																	<span className="flex items-center gap-2 min-w-0">
																		<UserCog className="w-4 h-4 text-muted-foreground shrink-0" />
																		<span className="text-xs font-medium uppercase tracking-wider truncate">
																			Accessi coach &amp; team
																		</span>
																		<Badge
																			variant="secondary"
																			className="rounded-sm shrink-0"
																		>
																			{accessCount}
																		</Badge>
																	</span>
																	<ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
																</button>
															</SheetTrigger>
															<SheetContent
																side="bottom"
																className="bg-card border-border rounded-t-sm max-h-[85vh] overflow-y-auto"
															>
																<SheetHeader className="text-left">
																	<SheetTitle className="font-heading">
																		Accessi coach &amp; team
																	</SheetTitle>
																	<SheetDescription>
																		Gestisci i coach e i coach team che possono
																		accedere a questo gruppo.
																	</SheetDescription>
																</SheetHeader>
																<div className="mt-4 space-y-5 pb-2">
																	{isGroupOwner ? (
																		accessControls
																	) : (
																		<p className="text-xs text-muted-foreground">
																			{t("groupSharingOwnerOnly")}
																		</p>
																	)}
																</div>
															</SheetContent>
														</Sheet>
													)}
												</div>

												{/* Current members */}
												{memberAthletes.length > 0 && (
													<div className="space-y-2 mb-3">
														<div className="flex items-center justify-between gap-3">
															<Label className="label-tactical text-xs">
																Atleti nel gruppo
															</Label>
															<Badge variant="secondary" className="rounded-sm">
																{sortedFilteredMemberAthletes.length}/
																{memberAthletes.length}
															</Badge>
														</div>
														<div className="rounded-sm border border-border/60 bg-background/60 max-h-[320px] overflow-y-auto">
															<div className="sticky top-0 z-10 bg-card/95 backdrop-blur border-b border-border/60 p-2 space-y-2">
																<div className="relative">
																	<Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
																	<Input
																		value={
																			groupMemberSearchQuery[group.group_id] ||
																			""
																		}
																		onChange={(e) =>
																			setGroupMemberSearchQuery((prev) => ({
																				...prev,
																				[group.group_id]: e.target.value,
																			}))
																		}
																		placeholder="Cerca atleta già nel gruppo"
																		className="pl-8 bg-secondary border-transparent rounded-sm h-8 text-xs"
																	/>
																</div>
																<Select
																	value={memberSort}
																	onValueChange={(value) =>
																		setGroupMemberSort((prev) => ({
																			...prev,
																			[group.group_id]: value,
																		}))
																	}
																>
																	<SelectTrigger className="bg-secondary border-transparent rounded-sm h-8 text-xs">
																		<SelectValue />
																	</SelectTrigger>
																	<SelectContent className="bg-card border-border">
																		<SelectItem value="last_access_desc">
																			Ultimo accesso (più recenti)
																		</SelectItem>
																		<SelectItem value="last_access_asc">
																			Ultimo accesso (meno recenti)
																		</SelectItem>
																		<SelectItem value="name_asc">
																			Nome (A-Z)
																		</SelectItem>
																		<SelectItem value="name_desc">
																			Nome (Z-A)
																		</SelectItem>
																	</SelectContent>
																</Select>
															</div>
															<div className="p-2 space-y-2">
																{sortedFilteredMemberAthletes.length > 0 ? (
																	sortedFilteredMemberAthletes.map((a) => (
																		<div
																			key={a.user_id}
																			className={`rounded-sm bg-secondary border px-3 py-2 flex items-center justify-between gap-3 transition-all ${
																				recentMemberChange?.athleteId ===
																					a.user_id &&
																				recentMemberChange?.action === "add"
																					? "border-volt/60 ring-1 ring-volt/40"
																					: "border-border/70"
																			}`}
																		>
																			<div className="min-w-0">
																				<p className="text-sm font-medium truncate">
																					{a.name}
																				</p>
																				<p className="text-xs text-muted-foreground truncate">
																					{a.email || "-"}
																				</p>
																				<p className="text-[10px] text-muted-foreground uppercase tracking-wider truncate">
																					Ultimo accesso:{" "}
																					{formatLastAccessDateTime(
																						a.last_access_at,
																					)}
																				</p>
																			</div>
																			<button
																				type="button"
																				className="hover:text-destructive"
																				onClick={() =>
																					toggleGroupAthlete(
																						group.group_id,
																						a.user_id,
																						{
																							requireConfirm: true,
																						},
																					)
																				}
																			>
																				<X className="w-3.5 h-3.5" />
																			</button>
																		</div>
																	))
																) : (
																	<p className="text-xs text-muted-foreground text-center py-3 rounded-sm bg-secondary/40 border border-border/50">
																		Nessun atleta trovato con questo filtro
																	</p>
																)}
															</div>
														</div>
													</div>
												)}
												{/* Available athletes */}
												<div className="space-y-1">
													<Label className="label-tactical text-xs">
														{t("addAthletes")}
													</Label>
													<Dialog
														open={groupAddAthleteId === group.group_id}
														onOpenChange={(open) => {
															if (open) {
																setGroupAddAthleteId(group.group_id);
																return;
															}
															setGroupAddAthleteId(null);
															setGroupAthleteSearchQuery("");
														}}
													>
														<DialogTrigger asChild>
															<Button
																variant="outline"
																className="w-full border-dashed border-volt/50 text-volt hover:bg-volt/10 rounded-sm h-8 text-sm"
															>
																<UserPlus className="w-4 h-4 mr-2" />
																{t("addAthletes")}
															</Button>
														</DialogTrigger>
														<DialogContent className="bg-card border-border rounded-sm max-w-sm">
															<DialogDescription className="hidden" />
															<DialogHeader>
																<DialogTitle className="font-heading">
																	{t("addAthletes")}
																</DialogTitle>
															</DialogHeader>
															<div className="space-y-3">
																<div className="relative">
																	<Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
																	<Input
																		value={groupAthleteSearchQuery}
																		onChange={(e) =>
																			setGroupAthleteSearchQuery(e.target.value)
																		}
																		placeholder={t("searchAthletes")}
																		className="pl-9 bg-secondary border-transparent rounded-sm"
																		autoFocus
																	/>
																</div>
																<ScrollArea className="h-[260px]">
																	{filteredAvailableGroupAthletes.length > 0 ? (
																		<div className="space-y-1">
																			{filteredAvailableGroupAthletes.map(
																				(a) => (
																					<button
																						key={a.user_id}
																						type="button"
																						className={`w-full flex items-center gap-3 px-3 py-2 hover:bg-white/5 rounded-sm text-left transition-all ${
																							recentMemberChange?.athleteId ===
																								a.user_id &&
																							recentMemberChange?.action ===
																								"remove"
																								? "ring-1 ring-blaze/40"
																								: ""
																						}`}
																						onClick={() => {
																							toggleGroupAthlete(
																								group.group_id,
																								a.user_id,
																							);
																							setGroupAddAthleteId(null);
																							setGroupAthleteSearchQuery("");
																						}}
																					>
																						<Users className="w-4 h-4 text-muted-foreground flex-shrink-0" />
																						<div className="min-w-0">
																							<p className="text-sm truncate">
																								{a.name}
																							</p>
																							<p className="text-xs text-muted-foreground truncate">
																								{a.email}
																							</p>
																							<p className="text-[10px] text-muted-foreground uppercase tracking-wider truncate">
																								Ultimo accesso:{" "}
																								{formatLastAccessDateTime(
																									a.last_access_at,
																								)}
																							</p>
																						</div>
																					</button>
																				),
																			)}
																		</div>
																	) : (
																		<p className="text-sm text-muted-foreground text-center py-6">
																			{availableGroupAthletes.length === 0
																				? "Tutti gli atleti sono gia nel gruppo"
																				: t("noAthletesFound")}
																		</p>
																	)}
																</ScrollArea>
															</div>
														</DialogContent>
													</Dialog>
												</div>
											</div>
										)}
									</CardContent>
								</Card>
							);
						})}
					</div>
				) : (
					<Card className="bg-card border-border rounded-sm">
						<CardContent className="flex flex-col items-center justify-center py-8 text-center">
							<FolderOpen className="w-10 h-10 text-muted-foreground mb-3" />
							<p className="font-heading text-base mb-1">{t("noGroups")}</p>
							<p className="text-sm text-muted-foreground">
								{t("createGroupHint")}
							</p>
						</CardContent>
					</Card>
				)}
			</div>

			{/* Search */}
			<div className="grid grid-cols-1 lg:grid-cols-4 gap-3 mb-6">
				<div className="flex items-center gap-2 lg:col-span-2">
					<div className="relative flex-1 min-w-0">
						<Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
						<Input
							placeholder={t("searchAthletes")}
							value={athleteSearch}
							onChange={(e) => setAthleteSearch(e.target.value)}
							className="pl-10 bg-secondary border-transparent h-12 rounded-sm"
							data-testid="athlete-search"
						/>
					</div>
					{/* Stessa scelta griglia/elenco della lista programmi. */}
					<div className="flex items-center gap-1 rounded-sm border border-border p-1 bg-secondary shrink-0 h-12">
						<Button
							type="button"
							variant={athletesViewMode === "grid" ? "default" : "ghost"}
							size="sm"
							className="h-9 rounded-sm px-2"
							onClick={() => setAthletesViewMode("grid")}
							title={t("viewModeGrid")}
							aria-label={t("viewModeGrid")}
							data-testid="athletes-view-grid"
						>
							<LayoutGrid className="w-4 h-4" />
						</Button>
						<Button
							type="button"
							variant={athletesViewMode === "list" ? "default" : "ghost"}
							size="sm"
							className="h-9 rounded-sm px-2"
							onClick={() => setAthletesViewMode("list")}
							title={t("viewModeList")}
							aria-label={t("viewModeList")}
							data-testid="athletes-view-list"
						>
							<List className="w-4 h-4" />
						</Button>
					</div>
				</div>

				<Select value={athleteSort} onValueChange={setAthleteSort}>
					<SelectTrigger className="bg-secondary border-transparent rounded-sm h-12">
						<SelectValue
							placeholder={
								language === "en" ? "Sort athletes" : "Ordina atleti"
							}
						/>
					</SelectTrigger>
					<SelectContent className="bg-card border-border">
						<SelectItem value="last_access_desc">
							{language === "en"
								? "Last access (recent first)"
								: "Ultimo accesso (piu recenti)"}
						</SelectItem>
						<SelectItem value="last_access_asc">
							{language === "en"
								? "Last access (oldest first)"
								: "Ultimo accesso (meno recenti)"}
						</SelectItem>
						<SelectItem value="name_asc">
							{language === "en" ? "Name (A-Z)" : "Nome (A-Z)"}
						</SelectItem>
						<SelectItem value="name_desc">
							{language === "en" ? "Name (Z-A)" : "Nome (Z-A)"}
						</SelectItem>
						<SelectItem value="created_desc">
							{language === "en"
								? "Newest registration"
								: "Registrazione piu recente"}
						</SelectItem>
					</SelectContent>
				</Select>

				<div className="grid grid-cols-2 gap-2">
					<Select
						value={athleteAccessFilter}
						onValueChange={setAthleteAccessFilter}
					>
						<SelectTrigger className="bg-secondary border-transparent rounded-sm h-12">
							<SelectValue
								placeholder={
									language === "en" ? "Access filter" : "Filtro accesso"
								}
							/>
						</SelectTrigger>
						<SelectContent className="bg-card border-border">
							<SelectItem value="all">
								{language === "en" ? "All accesses" : "Tutti gli accessi"}
							</SelectItem>
							<SelectItem value="recent_7">
								{language === "en"
									? "Active in 7 days"
									: "Attivi negli ultimi 7 giorni"}
							</SelectItem>
							<SelectItem value="recent_30">
								{language === "en"
									? "Active in 30 days"
									: "Attivi negli ultimi 30 giorni"}
							</SelectItem>
							<SelectItem value="older_30">
								{language === "en"
									? "Inactive for 30+ days"
									: "Inattivi da oltre 30 giorni"}
							</SelectItem>
							<SelectItem value="never">
								{language === "en" ? "Never accessed" : "Mai entrati"}
							</SelectItem>
						</SelectContent>
					</Select>

					<Select
						value={athleteSportFilter}
						onValueChange={setAthleteSportFilter}
					>
						<SelectTrigger className="bg-secondary border-transparent rounded-sm h-12">
							<SelectValue
								placeholder={language === "en" ? "Sport" : "Sport"}
							/>
						</SelectTrigger>
						<SelectContent className="bg-card border-border">
							<SelectItem value="all">{t("sportsFilterAll")}</SelectItem>
							{uniqueAthleteSports.map((sport) => (
								<SelectItem key={sport} value={sport}>
									{formatSportLabel(sport, sportsOptions)}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
			</div>

			{/* Programs Preview Dialog */}
			<AthleteViewPreview
				open={isAthleteProgramsPreviewOpen}
				onOpenChange={(open) => {
					setIsAthleteProgramsPreviewOpen(open);
					if (!open) {
						setAthleteProgramsPreviewState(null);
					}
				}}
				title={`${t("athleteProgramsPreviewTitle")} · ${selectedAthleteInsight?.name || ""}`}
				aside={
					<div className="rounded-sm border border-border bg-secondary/30 p-3 space-y-2">
						<p className="text-xs uppercase tracking-wider text-muted-foreground">
							Contesto anteprima
						</p>
						<div className="grid gap-2 text-xs">
							<div className="rounded-sm bg-background border border-border px-2 py-1.5">
								<span className="text-muted-foreground">Programma:</span>{" "}
								{athleteProgramsPreviewState?.program?.title || "-"}
							</div>
							<div className="rounded-sm bg-background border border-border px-2 py-1.5">
								<span className="text-muted-foreground">Tipo:</span>{" "}
								{athleteProgramsPreviewState?.program?.type === "group"
									? "Gruppo"
									: "Individuale"}
							</div>
							<div className="rounded-sm bg-background border border-border px-2 py-1.5">
								<span className="text-muted-foreground">
									Programmi assegnati:
								</span>{" "}
								{athleteProgramsPreviewState?.programCount || 0}
							</div>
						</div>
					</div>
				}
				previewState={
					athleteProgramsPreviewState?.program
						? {
								program: athleteProgramsPreviewState.program,
								programs: athleteProgramsPreviewState.programs || [],
								exercises,
								selectedProgramId:
									athleteProgramsPreviewState.program?.program_id || null,
								weekIndex: athleteProgramsPreviewState.weekIndex || 0,
								athleteId: athleteProgramsPreviewState.athleteId || null,
								athleteName:
									athleteProgramsPreviewState.athleteName ||
									selectedAthleteInsight?.name ||
									null,
							}
						: null
				}
				emptyLabel={t("noAssignedProgramsPreview")}
			/>

			{/* Agenda Dialog */}
			<Dialog
				open={isAthleteAgendaOpen}
				onOpenChange={(open) => {
					setIsAthleteAgendaOpen(open);
					if (!open) {
						setAthleteAgendaResults([]);
						setSelectedAthleteInsight(null);
					}
				}}
			>
				<DialogContent className="bg-card border-border max-w-2xl max-h-[80vh] overflow-hidden">
					<DialogDescription className="hidden" />
					<DialogHeader>
						<DialogTitle className="font-heading">
							{t("athleteAgendaResults")} · {selectedAthleteInsight?.name}
						</DialogTitle>
					</DialogHeader>
					<ScrollArea className="h-[55vh] pr-3">
						{loadingAthleteAgenda ? (
							<div className="flex items-center justify-center py-10 text-muted-foreground">
								<Loader2 className="w-5 h-5 animate-spin mr-2" />
								{t("loading")}
							</div>
						) : athleteAgendaResults.length === 0 ? (
							<p className="text-sm text-muted-foreground py-2">
								{t("noAgendaResults")}
							</p>
						) : (
							<div className="space-y-2">
								{athleteAgendaResults.map((result) => {
									const prescription =
										result?.prescription?.exercise ||
										result?.conditioning_config ||
										null;
									const conditioningView =
										resolveConditioningResultPresentation({
											result,
											prescriptionExercise: prescription,
											programExercise: null,
											language,
										});
									const conditioningType = conditioningView.condType;
									const isConditioningPrescription =
										conditioningView.isConditioning;
									const conditioningExercises = conditioningView.condExercises;
									const emomRounds = conditioningView.emomRounds;
									const displayScore = conditioningView.displayScore;
									const displayNotes = conditioningView.displayNotes;
									const prescriptionRows = Array.isArray(prescription?.set_rows)
										? prescription.set_rows
										: [];
									const resultRows = Array.isArray(result?.result_set_rows)
										? result.result_set_rows
										: [];
									const hasRows = resultRows.some(
										(row) => row?.weight_kg || row?.reps || row?.rpe,
									);

									return (
										<div
											key={result.result_id}
											className="border border-border rounded-sm p-3 space-y-2"
										>
											<div className="flex items-center justify-between gap-2">
												<p className="font-heading text-sm truncate">
													{result.exercise_name || "-"}
												</p>
												<Badge className="rounded-sm bg-volt/20 text-volt border-0">
													{result.date}
												</Badge>
											</div>

											{(result?.prescription?.program_name ||
												result?.prescription?.day_name) && (
												<div className="text-[11px] text-muted-foreground flex flex-wrap gap-x-3 gap-y-1">
													{result?.prescription?.program_name && (
														<span>
															{t("programLabel")}:{" "}
															{result.prescription.program_name}
														</span>
													)}
													{result?.prescription?.day_name && (
														<span>{result.prescription.day_name}</span>
													)}
												</div>
											)}

											{prescription && (
												<div className="bg-secondary/40 border border-border/70 rounded-sm p-2">
													<p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
														{t("prescriptionLabel")}
													</p>
													<div className="text-xs text-muted-foreground flex flex-wrap gap-x-3 gap-y-1">
														{!isConditioningPrescription &&
															prescription.methodology && (
																<span>
																	{t("methodology")}: {prescription.methodology}
																</span>
															)}
														{isConditioningPrescription &&
															hasContent(conditioningType) && (
																<span>
																	{t("methodology")}:{" "}
																	{String(conditioningType).toUpperCase()}
																</span>
															)}
														{!isConditioningPrescription &&
															prescription.sets && (
																<span>
																	{t("setsShort")}: {prescription.sets}
																</span>
															)}
														{!isConditioningPrescription &&
															prescription.reps && (
																<span>
																	{t("repsLabel")}: {prescription.reps}
																</span>
															)}
														{!isConditioningPrescription &&
															prescription.load_percentage && (
																<span>{prescription.load_percentage}%</span>
															)}
														{!isConditioningPrescription &&
															prescription.load_kg && (
																<span>{prescription.load_kg} kg</span>
															)}
														{prescription.time_seconds && (
															<span>
																{t("timeLabel")}:{" "}
																{formatProfileMetricValue(
																	prescription.time_seconds,
																	"seconds",
																)}
															</span>
														)}
														{prescription.rest_seconds && (
															<span>
																{t("restLabel")}:{" "}
																{formatProfileMetricValue(
																	prescription.rest_seconds,
																	"seconds",
																)}
															</span>
														)}
														{prescription.rpe && (
															<span>RPE: {prescription.rpe}</span>
														)}
														{prescription.rir && (
															<span>RIR: {prescription.rir}</span>
														)}
														{isConditioningPrescription &&
															prescription.time_cap_seconds && (
																<span>
																	TC{" "}
																	{formatProfileMetricValue(
																		prescription.time_cap_seconds,
																		"seconds",
																	)}
																</span>
															)}
													</div>
													{prescriptionRows.length > 0 && (
														<div className="space-y-1 mt-1">
															{prescriptionRows.map((row, idx) => (
																<div
																	key={idx}
																	className="text-xs text-muted-foreground border-l-2 border-border pl-2 flex flex-wrap gap-x-3 gap-y-1"
																>
																	<span className="text-[10px] uppercase tracking-wider">
																		{t("setsShort")} {idx + 1}
																	</span>
																	{row?.reps && (
																		<span>
																			{t("repsLabel")}: {row.reps}
																		</span>
																	)}
																	{row?.load_percentage && (
																		<span>{row.load_percentage}%</span>
																	)}
																	{row?.load_kg && (
																		<span>{row.load_kg} kg</span>
																	)}
																</div>
															))}
														</div>
													)}
													{Array.isArray(prescription.superset_exercises) &&
														prescription.superset_exercises.length > 0 && (
															<div className="text-xs text-muted-foreground flex flex-wrap gap-x-2 gap-y-1 mt-1">
																{prescription.superset_exercises.map(
																	(item, idx) => (
																		<span key={idx}>
																			{idx > 0 && " + "}
																			{item?.exercise_name}
																			{item?.reps ? ` (${item.reps})` : ""}
																		</span>
																	),
																)}
															</div>
														)}
													{conditioningType === "emom" &&
														emomRounds.length > 0 && (
															<div className="pl-0 space-y-1.5 mt-1">
																{emomRounds.map((round, j) => (
																	<div
																		key={round.round_id || j}
																		className="border border-yellow-500/20 bg-yellow-500/10 rounded-sm px-2 py-1.5"
																	>
																		<div className="text-[10px] uppercase tracking-wider text-yellow-400 font-medium mb-1">
																			{getEmomRoundLabel(round, j, language)}
																		</div>
																		<div className="space-y-0.5">
																			{round.items.map((item, itemIndex) => (
																				<span
																					key={
																						item.item_id || `${j}-${itemIndex}`
																					}
																					className="block text-xs text-muted-foreground"
																				>
																					{formatEmomItemSummary(
																						item,
																						language,
																					)}
																				</span>
																			))}
																		</div>
																	</div>
																))}
															</div>
														)}
													{(conditioningType !== "emom" ||
														emomRounds.length === 0) &&
														conditioningExercises.length > 0 && (
															<div className="text-xs text-muted-foreground space-y-1 mt-1">
																{conditioningExercises.map((item, idx) => (
																	<div key={idx}>
																		{item?.reps ? `${item.reps} ` : ""}
																		{item?.exercise_name || item?.name}
																	</div>
																))}
															</div>
														)}
													{conditioningType === "emom" && (
														<div className="text-xs space-y-1 mt-2 pt-2 border-t border-border/60">
															{conditioningView.intervalLabel && (
																<div className="text-volt">
																	{t("everyLabel")}{" "}
																	{conditioningView.intervalLabel}
																</div>
															)}
															{conditioningView.rounds !== null &&
																conditioningView.rounds !== undefined &&
																String(conditioningView.rounds).trim() !== "" &&
																String(conditioningView.rounds).trim() !==
																	"0" && (
																	<div className="text-volt">
																		{conditioningView.rounds}{" "}
																		{Number(conditioningView.rounds) === 1
																			? "round"
																			: "rounds"}
																	</div>
																)}
														</div>
													)}
													{prescription.notes && (
														<p className="text-xs text-muted-foreground whitespace-pre-wrap border-t border-border/60 pt-1 mt-1">
															{t("coachNotes")}: {prescription.notes}
														</p>
													)}
												</div>
											)}

											<div className="bg-green-500/10 border border-green-500/20 rounded-sm p-2">
												<p className="text-[10px] uppercase tracking-wider text-green-400 mb-1">
													{t("resultLabel")}
												</p>
												{Array.isArray(result.superset_details) &&
													result.superset_details.length > 0 && (
														<div className="space-y-1 mb-1">
															{result.superset_details.map((d, idx) => (
																<div
																	key={idx}
																	className="text-xs text-muted-foreground border-l-2 border-green-500/30 pl-2 flex flex-wrap gap-x-3 gap-y-1"
																>
																	<span className="text-[10px] uppercase tracking-wider text-green-400">
																		{String.fromCharCode(65 + idx)}
																	</span>
																	<span className="font-medium text-foreground">
																		{d.exercise_name}
																	</span>
																	{d.weight_kg && <span>{d.weight_kg} kg</span>}
																	{d.reps && (
																		<span>
																			{t("repsLabel")}: {d.reps}
																		</span>
																	)}
																	{d.rpe && <span>RPE {d.rpe}</span>}
																</div>
															))}
														</div>
													)}
												{hasRows && (
													<div className="space-y-1 mb-1">
														{resultRows.map((row, idx) => {
															const hasRow =
																row?.weight_kg || row?.reps || row?.rpe;
															if (!hasRow) return null;
															return (
																<div
																	key={idx}
																	className="text-xs text-muted-foreground border-l-2 border-green-500/30 pl-2 flex flex-wrap gap-x-3 gap-y-1"
																>
																	<span className="text-[10px] uppercase tracking-wider text-green-400">
																		{t("setsShort")} {idx + 1}
																	</span>
																	{row.weight_kg && (
																		<span>{row.weight_kg} kg</span>
																	)}
																	{row.reps && (
																		<span>
																			{t("repsLabel")}: {row.reps}
																		</span>
																	)}
																	{row.rpe && <span>RPE {row.rpe}</span>}
																</div>
															);
														})}
													</div>
												)}
												<div className="text-xs text-muted-foreground flex flex-wrap gap-x-3 gap-y-1">
													{result.weight_kg !== null &&
														result.weight_kg !== undefined && (
															<span>
																{t("weightKgLabel")}: {result.weight_kg}
															</span>
														)}
													{hasIntervalScores(result) && (
														<span>
															Ripetute: {result.interval_scores.join(" · ")}
														</span>
													)}
													{!hasIntervalScores(result) &&
														(result.reps_text || result.reps) && (
															<span>
																{t("repsLabel")}:{" "}
																{result.reps_text || result.reps}
															</span>
														)}
													{result.time_seconds !== null &&
														result.time_seconds !== undefined && (
															<span>
																{t("timeLabel")}:{" "}
																{formatProfileMetricValue(
																	result.time_seconds,
																	"seconds",
																)}
															</span>
														)}
													{hasContent(displayScore) && (
														<span>
															{t("scoreLabel")}: {displayScore}
														</span>
													)}
													{result.rpe !== null && result.rpe !== undefined && (
														<span>RPE: {result.rpe}</span>
													)}
												</div>
											</div>

											<div className="bg-volt/5 border border-volt/20 rounded-sm p-2">
												<p className="text-[10px] uppercase tracking-wider text-volt mb-1">
													{t("workoutFeedback")}
												</p>
												{displayNotes ? (
													<p className="text-xs text-muted-foreground whitespace-pre-wrap">
														{displayNotes}
													</p>
												) : (
													<p className="text-xs text-muted-foreground">
														{t("noFeedbackLabel")}
													</p>
												)}
											</div>
										</div>
									);
								})}
							</div>
						)}
					</ScrollArea>
				</DialogContent>
			</Dialog>

			{/* Calendar Dialog */}
			<Dialog
				open={isAthleteCalendarOpen}
				onOpenChange={(open) => {
					setIsAthleteCalendarOpen(open);
					if (!open) {
						setAthleteCompetitionsPreview([]);
						setSelectedAthleteInsight(null);
					}
				}}
			>
				<DialogContent className="bg-card border-border max-w-2xl max-h-[80vh] overflow-hidden">
					<DialogDescription className="hidden" />
					<DialogHeader>
						<DialogTitle className="font-heading">
							{t("athleteCompetitionCalendar")} · {selectedAthleteInsight?.name}
						</DialogTitle>
					</DialogHeader>
					<ScrollArea className="h-[55vh] pr-3">
						{loadingAthleteCalendar ? (
							<div className="flex items-center justify-center py-10 text-muted-foreground">
								<Loader2 className="w-5 h-5 animate-spin mr-2" />
								{t("loading")}
							</div>
						) : athleteCompetitionsPreview.length === 0 ? (
							<p className="text-sm text-muted-foreground py-2">
								{t("noCompetitionEvents")}
							</p>
						) : (
							<div className="space-y-2">
								{athleteCompetitionsPreview.map((event) => (
									<div
										key={event.event_id}
										className="border border-border rounded-sm p-3"
									>
										<div className="flex items-center justify-between gap-2">
											<p className="font-heading text-sm">{event.name}</p>
											<div className="flex items-center gap-1 shrink-0">
												{(event.shared_athlete_count || 1) >= 2 && (
													<SharedAthletesBadge
														count={event.shared_athlete_count}
														onClick={() => openCompetitionParticipants(event)}
													/>
												)}
												<Badge className="rounded-sm bg-volt/20 text-volt border-0">
													{event.date}
												</Badge>
												<button
													type="button"
													onClick={() => openEditCompetition(event)}
													className="text-muted-foreground hover:text-volt p-0.5"
													aria-label={t("edit")}
												>
													<Edit className="w-3.5 h-3.5" />
												</button>
												<button
													type="button"
													onClick={() => deleteCompetitionEvent(event)}
													className="text-muted-foreground hover:text-blaze p-0.5"
													aria-label={t("delete")}
												>
													<Trash2 className="w-3.5 h-3.5" />
												</button>
											</div>
										</div>
										{event.event_type && (
											<p className="text-xs text-muted-foreground mt-1">
												{event.event_type}
											</p>
										)}
										{event.location && (
											<p className="text-xs text-muted-foreground">
												{event.location}
											</p>
										)}
										{event.notes && (
											<p className="text-xs text-muted-foreground mt-1 line-clamp-2">
												{event.notes}
											</p>
										)}
									</div>
								))}
							</div>
						)}
					</ScrollArea>
				</DialogContent>
			</Dialog>

			<CompetitionParticipantsDialog
				open={Boolean(participantsEvent)}
				onOpenChange={(open) => {
					if (!open) setParticipantsEvent(null);
				}}
				event={participantsEvent}
				athletes={participants}
				loading={participantsLoading}
			/>

			<CompetitionEventDialog
				open={Boolean(editingCompetition)}
				onOpenChange={(open) => {
					if (!open) setEditingCompetition(null);
				}}
				form={competitionForm}
				setForm={setCompetitionForm}
				onSave={saveCompetitionEvent}
				isEditing
			/>

			{/* Wellness (health diary) coach dialog */}
			<WellnessCoachDialog
				athlete={wellnessAthlete}
				open={isWellnessOpen}
				onOpenChange={(open) => {
					setIsWellnessOpen(open);
					if (!open) setWellnessAthlete(null);
				}}
			/>

			{/* Wellness (health diary) bulk enablement: many athletes / whole groups */}
			<WellnessBulkDialog
				open={isWellnessBulkOpen}
				onOpenChange={(open) => {
					setIsWellnessBulkOpen(open);
					if (!open) setWellnessBulkGroupId(null);
				}}
				athletes={filteredAthletes}
				groups={filteredGroups}
				initialGroupId={wellnessBulkGroupId}
				scope={isAdminUser && athletesScope === "all" ? "all" : "mine"}
			/>

			{/* Athlete Cards */}
			{filteredAthletes.length > 0 ? (
				<div
					className={
						athletesViewMode === "list"
							? "space-y-2"
							: "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
					}
				>
					{filteredAthletes.map((athlete) => {
						const formattedLastAccess = formatLastAccessDateTime(
							athlete.last_access_at,
						);
						const athleteAvatarUrl = getImageUrl(athlete.picture || "");
						const lastAccessLabel =
							language === "en" ? "Last access" : "Ultimo accesso";
						const athleteSportsLabel = athleteSportsOf(athlete)
							.map((sport) => formatSportLabel(sport, sportsOptions))
							.join(" · ");

						// Perche' questo atleta e' qui. Senza, un coach si trova in elenco
						// persone che non ha assegnato lui e non sa da dove arrivino.
						const sharedViaBadge = (() => {
							const directCoachIds = [
								...(athlete?.coach_ids || []),
								...(athlete?.coach_id ? [athlete.coach_id] : []),
							];
							if (directCoachIds.includes(user?.user_id)) return null;
							const viaTeams = (athlete?.coach_team_ids || [])
								.map((id) => myCoachTeamNameById[id])
								.filter(Boolean);
							if (viaTeams.length === 0) return null;
							return (
								<span className="flex items-center gap-1 text-[10px] text-acid">
									<Building2 className="w-3 h-3 shrink-0" />
									<span className="truncate">
										{t("programViewSharedVia")}: {viaTeams.join(", ")}
									</span>
								</span>
							);
						})();

						// Griglia ed elenco mostrano le stesse azioni: definite una volta.
						const quickActionButtons = (
							<TooltipProvider delayDuration={90}>
								<Tooltip>
									<TooltipTrigger asChild>
										<Button
											variant="ghost"
											size="icon"
											className="rounded-sm h-8 w-8 shrink-0 text-muted-foreground hover:text-volt"
											onClick={() => openAthleteProfilePreview(athlete)}
										>
											<LineChart className="w-4 h-4" />
										</Button>
									</TooltipTrigger>
									<TooltipContent>{t("openPerformanceProfile")}</TooltipContent>
								</Tooltip>
								<Tooltip>
									<TooltipTrigger asChild>
										<Button
											variant="ghost"
											size="icon"
											className="rounded-sm h-8 w-8 text-muted-foreground hover:text-volt"
											onClick={() => openAthleteCalendarPreview(athlete)}
										>
											<CalendarDays className="w-4 h-4" />
										</Button>
									</TooltipTrigger>
									<TooltipContent>
										{t("openCompetitionCalendar")}
									</TooltipContent>
								</Tooltip>
								<Tooltip>
									<TooltipTrigger asChild>
										<Button
											variant="ghost"
											size="icon"
											className="rounded-sm h-8 w-8 shrink-0 text-muted-foreground hover:text-volt"
											onClick={() => openAthleteAgendaPreview(athlete)}
										>
											<ClipboardCheck className="w-4 h-4" />
										</Button>
									</TooltipTrigger>
									<TooltipContent>{t("openAthleteAgenda")}</TooltipContent>
								</Tooltip>
								<Tooltip>
									<TooltipTrigger asChild>
										<Button
											variant="ghost"
											size="icon"
											className="rounded-sm h-8 w-8 shrink-0 text-muted-foreground hover:text-volt"
											onClick={() => {
												setWellnessAthlete(athlete);
												setIsWellnessOpen(true);
											}}
										>
											<HeartPulse className="w-4 h-4" />
										</Button>
									</TooltipTrigger>
									<TooltipContent>{t("wellnessDiary")}</TooltipContent>
								</Tooltip>
								{athletesViewMode === "list" && (
									<Tooltip>
										<TooltipTrigger asChild>
											<Button
												variant="ghost"
												size="icon"
												className="rounded-sm h-8 w-8 shrink-0 text-muted-foreground hover:text-volt"
												onClick={() => openAthleteProgramsPreview(athlete)}
											>
												<Eye className="w-4 h-4" />
											</Button>
										</TooltipTrigger>
										<TooltipContent>
											{t("athleteProgramsPreviewCta")}
										</TooltipContent>
									</Tooltip>
								)}
							</TooltipProvider>
						);
						const editAthleteDialog = (
							<Dialog
								open={
									isEditAthleteOpen &&
									selectedAthlete?.user_id === athlete.user_id
								}
								onOpenChange={(open) => {
									setIsEditAthleteOpen(open);
									if (open) setSelectedAthlete(athlete);
								}}
							>
								<TooltipProvider delayDuration={90}>
									<Tooltip>
										<TooltipTrigger asChild>
											<DialogTrigger asChild>
												<Button
													variant="ghost"
													size="icon"
													className="rounded-sm h-8 w-8 shrink-0 text-muted-foreground hover:text-foreground"
													data-testid={`edit-athlete-${athlete.user_id}`}
												>
													<Edit className="w-4 h-4" />
												</Button>
											</DialogTrigger>
										</TooltipTrigger>
										<TooltipContent>{t("editAthlete")}</TooltipContent>
									</Tooltip>
								</TooltipProvider>
								<DialogContent className="bg-card border-border">
									<DialogDescription className="hidden" />
									<DialogHeader>
										<DialogTitle className="font-heading">
											{t("editAthlete")}
										</DialogTitle>
									</DialogHeader>
									<div className="space-y-3 rounded-sm border border-border bg-secondary/40 p-3">
										<div className="flex items-center justify-between gap-2">
											<p className="label-tactical">Notifiche push</p>
											{isLoadingAthleteNotificationPrefs ? (
												<Badge variant="outline" className="rounded-sm text-xs">
													Caricamento
												</Badge>
											) : selectedAthleteNotificationPrefs?.push_enabled ? (
												<Badge className="rounded-sm bg-green-500/20 text-green-400 border-0 text-xs">
													Attive
												</Badge>
											) : selectedAthleteNotificationPrefs?.push_ever_enabled ? (
												<Badge variant="outline" className="rounded-sm text-xs">
													Disattivate
												</Badge>
											) : (
												<Badge variant="outline" className="rounded-sm text-xs">
													Mai configurate
												</Badge>
											)}
										</div>
										<div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
											<span>
												Messaggi:{" "}
												{selectedAthleteNotificationPrefs?.push_new_message
													? "attivi"
													: "disattivati"}
											</span>
											<span>
												Storico onboarding:{" "}
												{selectedAthleteNotificationPrefs?.push_ever_enabled
													? "sì"
													: "no"}
											</span>
											{selectedAthleteNotificationPrefs?.updated_at && (
												<span>
													Aggiornate il{" "}
													{new Date(
														selectedAthleteNotificationPrefs.updated_at,
													).toLocaleDateString(
														language === "en" ? "en-US" : "it-IT",
													)}
												</span>
											)}
										</div>
									</div>
									<form onSubmit={handleUpdateAthlete} className="space-y-4">
										<div className="space-y-2">
											<Label className="label-tactical">
												{t("sportLabel")}
											</Label>
											<p className="text-xs text-muted-foreground">
												{t("sportsMultiHint")}
											</p>
											{/* Lo stesso selettore del Profilo atleta: chip
											    multiple, la prima e' la principale. */}
											<SportsPicker
												options={sportsOptions}
												value={editSportsOf(selectedAthlete)}
												onChange={(next) =>
													setSelectedAthlete({
														...selectedAthlete,
														sports: next,
														_sportsTouched: true,
													})
												}
											/>
											<SportsExplainer audience="athlete" forCoach />
										</div>
										<div className="space-y-2">
											<Label className="label-tactical">
												{t("genderLabel")}
											</Label>
											<Select
												value={selectedAthlete?.gender || "none"}
												onValueChange={(value) =>
													setSelectedAthlete({
														...selectedAthlete,
														gender: value === "none" ? null : value,
													})
												}
											>
												<SelectTrigger className="bg-secondary border-transparent rounded-sm">
													<SelectValue placeholder={t("selectGender")} />
												</SelectTrigger>
												<SelectContent>
													<SelectItem value="none">
														{t("selectGender")}
													</SelectItem>
													<SelectItem value="male">
														{t("genderMale")}
													</SelectItem>
													<SelectItem value="female">
														{t("genderFemale")}
													</SelectItem>
												</SelectContent>
											</Select>
										</div>
										<div className="space-y-2">
											<Label className="label-tactical">Peso (kg)</Label>
											<Input
												type="number"
												step="0.1"
												value={selectedAthlete?.weight || ""}
												onChange={(e) =>
													setSelectedAthlete({
														...selectedAthlete,
														weight: e.target.value,
													})
												}
												className="bg-secondary border-transparent rounded-sm"
												placeholder="75.5"
											/>
										</div>
										<div className="space-y-2">
											<Label className="label-tactical">Note</Label>
											<Textarea
												value={selectedAthlete?.notes || ""}
												onChange={(e) =>
													setSelectedAthlete({
														...selectedAthlete,
														notes: e.target.value,
													})
												}
												className="bg-secondary border-transparent rounded-sm min-h-[100px]"
												placeholder="Note sull'atleta..."
											/>
										</div>
										<div className="flex items-center justify-between gap-3 p-3 rounded-sm bg-secondary border border-border">
											<div className="flex items-center gap-2 min-w-0">
												{selectedAthlete?.performance_public ? (
													<Globe className="w-4 h-4 text-volt flex-shrink-0" />
												) : (
													<EyeOff className="w-4 h-4 text-muted-foreground flex-shrink-0" />
												)}
												<div className="min-w-0">
													<p className="text-xs uppercase tracking-widest text-muted-foreground">
														{t("performancePublicLabel")}
													</p>
													<p className="text-xs text-muted-foreground">
														{t("performancePublicDescription")}
													</p>
												</div>
											</div>
											<Switch
												checked={!!selectedAthlete?.performance_public}
												onCheckedChange={(value) =>
													setSelectedAthlete({
														...selectedAthlete,
														performance_public: !!value,
													})
												}
											/>
										</div>
										<div className="flex items-center justify-between gap-3 p-3 rounded-sm bg-secondary border border-border">
											<div className="flex items-center gap-2 min-w-0">
												{selectedAthlete?.results_public !== false ? (
													<Globe className="w-4 h-4 text-volt flex-shrink-0" />
												) : (
													<EyeOff className="w-4 h-4 text-muted-foreground flex-shrink-0" />
												)}
												<div className="min-w-0">
													<p className="text-xs uppercase tracking-widest text-muted-foreground">
														{t("resultsPublicLabel")}
													</p>
													<p className="text-xs text-muted-foreground">
														{t("resultsPublicDescription")}
													</p>
												</div>
											</div>
											<Switch
												checked={selectedAthlete?.results_public !== false}
												onCheckedChange={(value) =>
													setSelectedAthlete({
														...selectedAthlete,
														results_public: !!value,
													})
												}
											/>
										</div>
										<Button
											type="submit"
											className="w-full bg-volt hover:bg-volt/90 rounded-sm"
										>
											{t("saveChanges")}
										</Button>
									</form>
								</DialogContent>
							</Dialog>
						);

						// ── Programmi assegnati ──
						const athletePrograms = getAthleteAssignedPrograms(athlete);
						const hasActiveProgram = athletePrograms.some(
							(prog) =>
								getProgramVisibilityStats(prog, athlete.user_id)
									.hasVisibleWeeks,
						);
						const isProgramsExpanded =
							!!expandedAthletePrograms[athlete.user_id];
						const assignedProgramLinks = (
							<div className="space-y-1 min-w-0">
								{athletePrograms.map((prog) => {
									const visibilityStats = getProgramVisibilityStats(
										prog,
										athlete.user_id,
									);
									const hasPublished = visibilityStats.hasVisibleWeeks;
									const publishedWeeks = visibilityStats.visibleWeeks;
									const totalWeeks = visibilityStats.totalWeeks;
									return (
										<Link
											key={prog.program_id}
											to={`/programs/${prog.program_id}`}
											className="flex items-center gap-2 p-1.5 rounded-sm hover:bg-white/5 group min-w-0 overflow-hidden"
										>
											<div
												className={`w-2 h-2 rounded-full shrink-0 ${
													hasPublished
														? "bg-green-400"
														: "bg-muted-foreground/40"
												}`}
											/>
											<div className="flex-1 min-w-0 flex flex-col gap-0.5 sm:flex-row sm:items-center sm:gap-2">
												<p className="text-[13px] font-medium truncate group-hover:text-volt">
													{prog.title}
												</p>
												<div className="text-[10px] text-muted-foreground leading-tight break-words sm:whitespace-nowrap">
													{hasPublished ? (
														<span className="text-green-400">
															{t("programActiveShort")} · {publishedWeeks}/
															{totalWeeks} {t("programWeeksShort")}
														</span>
													) : (
														<span>
															{t("programInactiveShort")} · {totalWeeks}{" "}
															{t("programWeeksShort")}
														</span>
													)}
												</div>
											</div>
											<ChevronRight className="w-4 h-4 text-muted-foreground shrink-0 opacity-0 group-hover:opacity-100" />
										</Link>
									);
								})}
							</div>
						);

						// ── Programmi assegnati (griglia) ──
						const assignedProgramsSection = (() => {
							if (athletePrograms.length === 0) return null;
							const isExpanded = isProgramsExpanded;
							return (
								<Collapsible
									open={isExpanded}
									onOpenChange={() => toggleAthletePrograms(athlete.user_id)}
								>
									<div className="mt-3 pt-3 border-t border-border/80">
										<CollapsibleTrigger asChild>
											<Button
												variant="ghost"
												className="w-full h-auto px-0 py-0 rounded-none hover:bg-transparent"
											>
												<div className="w-full flex items-center justify-between gap-2">
													<div className="flex items-center gap-2 min-w-0">
														<p className="label-tactical mb-0 text-[10px]">
															{t("assignedPrograms")} ({athletePrograms.length})
														</p>
														<div
															className={`w-2 h-2 rounded-full shrink-0 ${
																hasActiveProgram
																	? "bg-green-400"
																	: "bg-muted-foreground/40"
															}`}
														/>
														<span className="text-[11px] text-muted-foreground uppercase tracking-widest">
															{hasActiveProgram
																? t("programActiveShort")
																: t("programInactiveShort")}
														</span>
													</div>
													{isExpanded ? (
														<ChevronUp className="w-4 h-4 text-muted-foreground shrink-0" />
													) : (
														<ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />
													)}
												</div>
											</Button>
										</CollapsibleTrigger>
										<CollapsibleContent className="pt-1.5 min-w-0 overflow-hidden">
											{assignedProgramLinks}
										</CollapsibleContent>
									</div>
								</Collapsible>
							);
						})();

						if (athletesViewMode === "list") {
							const athleteSports = athleteSportsOf(athlete);
							const primarySportLabel = athleteSports.length
								? formatSportLabel(athleteSports[0], sportsOptions)
								: "";
							return (
								<Card
									key={athlete.user_id}
									className="bg-card border-border rounded-sm hover:border-volt/30 transition-colors"
									data-testid={`athlete-row-${athlete.user_id}`}
								>
									<Collapsible
										open={isProgramsExpanded}
										onOpenChange={() => toggleAthletePrograms(athlete.user_id)}
									>
										<CardContent className="p-3">
											{/* Una riga per atleta da lg in su, colonne a larghezza
											    fissa: niente va a capo, i testi lunghi si troncano e
											    si leggono per intero al passaggio del mouse. Sotto lg
											    i dati scendono in una riga sola che va a capo. */}
											<div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
												<div className="flex-1 min-w-0 flex flex-wrap items-center gap-x-4 gap-y-1 lg:grid lg:grid-cols-12 lg:gap-3">
													<Link
														to={`/athletes/${athlete.user_id}`}
														className="basis-full lg:basis-auto lg:col-span-4 flex items-center gap-3 min-w-0 group rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-volt"
													>
														<Avatar className="w-9 h-9 shrink-0">
															<AvatarImage
																src={athleteAvatarUrl || undefined}
															/>
															<AvatarFallback className="bg-volt text-white">
																{athlete.name?.charAt(0).toUpperCase()}
															</AvatarFallback>
														</Avatar>
														<div className="min-w-0">
															<p className="font-heading text-base leading-tight truncate group-hover:text-volt transition-colors">
																{formatFullName(athlete) || athlete.name}
															</p>
															<p
																className="text-xs text-muted-foreground truncate"
																title={athlete.email}
															>
																{athlete.email}
															</p>
															{sharedViaBadge}
														</div>
													</Link>
													<div
														className="lg:col-span-3 flex items-center gap-1.5 min-w-0 text-xs text-muted-foreground"
														title={athleteSportsLabel || undefined}
													>
														{primarySportLabel ? (
															<>
																<Activity className="w-3.5 h-3.5 shrink-0" />
																<span className="truncate">
																	{primarySportLabel}
																</span>
																{athleteSports.length > 1 && (
																	<AthleteSportsOverflow
																		labels={athleteSports
																			.slice(1)
																			.map((sport) =>
																				formatSportLabel(sport, sportsOptions),
																			)}
																	/>
																)}
															</>
														) : null}
													</div>
													<div className="lg:col-span-1 flex items-center gap-1 text-xs text-muted-foreground whitespace-nowrap">
														{athlete.weight ? (
															<>
																<Weight className="w-3.5 h-3.5 shrink-0" />
																{athlete.weight} kg
															</>
														) : null}
													</div>
													<div
														className="lg:col-span-2 flex items-center gap-1 text-xs text-muted-foreground whitespace-nowrap"
														title={`${lastAccessLabel}: ${formattedLastAccess}`}
													>
														<Clock className="w-3.5 h-3.5 shrink-0" />
														{formatLastAccessShort(athlete.last_access_at)}
													</div>
													<div className="lg:col-span-2 min-w-0">
														{athletePrograms.length > 0 ? (
															<CollapsibleTrigger asChild>
																<Button
																	variant="ghost"
																	size="sm"
																	className="h-7 px-1.5 -ml-1.5 rounded-sm gap-1.5 text-xs font-normal text-muted-foreground hover:text-foreground max-w-full"
																	title={`${t("assignedPrograms")} · ${
																		hasActiveProgram
																			? t("programActiveShort")
																			: t("programInactiveShort")
																	}`}
																>
																	<span
																		className={`w-2 h-2 rounded-full shrink-0 ${
																			hasActiveProgram
																				? "bg-green-400"
																				: "bg-muted-foreground/40"
																		}`}
																	/>
																	<span className="truncate">
																		{athletePrograms.length}{" "}
																		{t(
																			"athleteProgramsPreviewCta",
																		).toLowerCase()}
																	</span>
																	{isProgramsExpanded ? (
																		<ChevronUp className="w-3.5 h-3.5 shrink-0" />
																	) : (
																		<ChevronDown className="w-3.5 h-3.5 shrink-0" />
																	)}
																</Button>
															</CollapsibleTrigger>
														) : null}
													</div>
												</div>
												<div className="flex flex-wrap justify-end items-center gap-0.5 shrink-0 -mr-1 sm:mr-0">
													{quickActionButtons}
													{editAthleteDialog}
													{/* Il nome e' gia' un link, ma in elenco non si
													    capiva: l'ingresso al dettaglio si scrive. */}
													<Button
														asChild
														variant="outline"
														size="sm"
														className="basis-full sm:basis-auto mt-1 sm:mt-0 sm:ml-1 h-9 sm:h-8 rounded-sm gap-0.5 pl-2.5 pr-1.5 border-volt/40 text-volt hover:text-volt hover:border-volt/70"
													>
														<Link
															to={`/athletes/${athlete.user_id}`}
															data-testid={`athlete-detail-${athlete.user_id}`}
														>
															{t("athleteDetailCta")}
															<ChevronRight className="w-4 h-4" />
														</Link>
													</Button>
												</div>
											</div>
											<CollapsibleContent className="min-w-0 overflow-hidden">
												<div className="mt-2 pt-2 border-t border-border/80">
													{assignedProgramLinks}
												</div>
											</CollapsibleContent>
										</CardContent>
									</Collapsible>
								</Card>
							);
						}

						return (
							<Card
								key={athlete.user_id}
								className="bg-card border-border rounded-sm hover:border-volt/30 transition-colors"
								data-testid={`athlete-card-${athlete.user_id}`}
							>
								<CardContent className="p-4">
									{/* Su mobile la fila di icone a destra lasciava al nome una
									    manciata di pixel e lo troncava quasi sempre: sotto sm le
									    azioni scendono su una riga propria e il nome puo' andare
									    a capo. */}
									<div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-3">
										<Link
											to={`/athletes/${athlete.user_id}`}
											className="flex items-center gap-3 min-w-0 flex-1 group rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-volt"
										>
											<Avatar className="w-11 h-11">
												<AvatarImage src={athleteAvatarUrl || undefined} />
												<AvatarFallback className="bg-volt text-white">
													{athlete.name?.charAt(0).toUpperCase()}
												</AvatarFallback>
											</Avatar>
											<div className="min-w-0">
												<p className="font-heading text-lg leading-tight break-words line-clamp-2 sm:line-clamp-1 group-hover:text-volt transition-colors">
													{formatFullName(athlete) || athlete.name}
												</p>
												{sharedViaBadge}
											</div>
										</Link>
										<div className="flex flex-wrap justify-end items-center gap-0.5 shrink-0 -mr-1 sm:mr-0">
											{quickActionButtons}
											{editAthleteDialog}
										</div>
									</div>

									<TooltipProvider delayDuration={90}>
										<Tooltip>
											<TooltipTrigger asChild>
												<p className="text-sm text-muted-foreground leading-tight break-all">
													{athlete.email}
												</p>
											</TooltipTrigger>
											<TooltipContent>{athlete.email}</TooltipContent>
										</Tooltip>
									</TooltipProvider>
									<p className="text-xs text-muted-foreground/80 mt-1 leading-tight">
										{lastAccessLabel}: {formattedLastAccess}
									</p>

									<div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
										{athleteSportsLabel && (
											<div className="flex items-center gap-1.5 min-w-0">
												<Activity className="w-3.5 h-3.5 shrink-0" />
												<span className="truncate">{athleteSportsLabel}</span>
											</div>
										)}
										{athlete.weight && (
											<div className="flex items-center gap-1.5">
												<Weight className="w-3.5 h-3.5" />
												<span>{athlete.weight} kg</span>
											</div>
										)}
									</div>

									<div className="pt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
										<Button
											variant="outline"
											size="sm"
											className="w-full rounded-sm h-8 border-dashed border-volt/40 text-volt hover:text-volt hover:border-volt/70"
											onClick={() => navigate(`/athletes/${athlete.user_id}`)}
										>
											<ExternalLink className="w-3.5 h-3.5 mr-1" />
											{t("athleteDetailCta")}
										</Button>
										<Button
											variant="outline"
											size="sm"
											className="w-full rounded-sm h-8 border-dashed border-volt/40 text-volt hover:text-volt hover:border-volt/70"
											onClick={() => openAthleteProgramsPreview(athlete)}
										>
											<Eye className="w-3.5 h-3.5 mr-1" />
											{t("athleteProgramsPreviewCta")}
										</Button>
									</div>

									{assignedProgramsSection}
								</CardContent>
							</Card>
						);
					})}
				</div>
			) : (
				<Card className="bg-card border-border rounded-sm">
					<CardContent className="flex flex-col items-center justify-center py-12 text-center">
						<Users className="w-12 h-12 text-muted-foreground mb-4" />
						<p className="font-heading text-lg mb-2">{t("noAthletesFound")}</p>
						<p className="text-sm text-muted-foreground">
							{athleteSearch
								? "Prova con una ricerca diversa"
								: "Gli atleti appariranno qui quando si registreranno"}
						</p>
					</CardContent>
				</Card>
			)}
		</div>
	);
}
